import { $, createEl, jsonFetch, isSignedIn, clearSession } from '../source/browser-utils.js';

// script2.js
// Shared helpers + home + sidebar + footer year

(() => {
  if (window.lnfAuthGate || window.__lnfAuthGateInitialized || window.__lnfAuthGateLoading) return;
  const existing = document.querySelector('script[data-lnf-auth-gate]');
  if (existing) return;
  const script = document.createElement('script');
  script.src = 'auth-gate.js';
  script.defer = true;
  script.dataset.lnfAuthGate = '1';
  window.__lnfAuthGateLoading = true;
  script.addEventListener('load', () => {
    window.__lnfAuthGateLoading = false;
  });
  script.addEventListener('error', () => {
    window.__lnfAuthGateLoading = false;
  });
  document.head.appendChild(script);
})();

(() => {
  if (window.lnfNotifications || window.__lnfNotificationsInitialized || window.__lnfNotificationsLoading) return;
  const existing = document.querySelector('script[data-lnf-notifications]');
  if (existing) return;
  const script = document.createElement('script');
  script.src = 'notifications.js';
  script.defer = true;
  script.dataset.lnfNotifications = '1';
  window.__lnfNotificationsLoading = true;
  script.addEventListener('load', () => {
    window.__lnfNotificationsLoading = false;
  });
  script.addEventListener('error', () => {
    window.__lnfNotificationsLoading = false;
  });
  document.head.appendChild(script);
})();

(() => {
  if (window.lnfUi) return;

  const TOAST_STACK_ID = 'lnfToastStack';

  const ensureToastStack = () => {
    let stack = document.getElementById(TOAST_STACK_ID);
    if (stack) return stack;
    stack = document.createElement('div');
    stack.id = TOAST_STACK_ID;
    stack.className = 'lnf-toast-stack';
    document.body.appendChild(stack);
    return stack;
  };

  const toast = (message, options = {}) => {
    if (!message) return;
    const type = String(options.type || 'info').toLowerCase();
    const sticky = Boolean(options.sticky || type === 'error');
    const duration = Number(options.duration || 3200);
    const stack = ensureToastStack();
    const item = document.createElement('div');
    item.className = `lnf-toast${type === 'error' ? ' lnf-toast--error' : ''}`;
    const text = document.createElement('span');
    text.textContent = String(message);
    item.appendChild(text);
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'lnf-toast__dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss notification');
    dismiss.textContent = 'x';
    dismiss.addEventListener('click', () => item.remove());
    item.appendChild(dismiss);
    stack.appendChild(item);
    if (!sticky) {
      window.setTimeout(() => item.remove(), Math.max(800, duration));
    }
  };

  const makeDialog = ({ title = 'Notice', message = '', variant = 'modal' }) => {
    const isSheet = variant === 'sheet';
    const backdrop = document.createElement('div');
    backdrop.className = `lnf-dialog-backdrop${isSheet ? ' lnf-dialog-backdrop--sheet' : ''}`;
    const card = document.createElement('div');
    card.className = `lnf-dialog-card${isSheet ? ' lnf-dialog-card--sheet' : ''}`;
    const heading = document.createElement('h3');
    heading.className = 'lnf-dialog-title';
    heading.textContent = title;
    const body = document.createElement('p');
    body.className = 'lnf-dialog-message';
    body.textContent = message;
    card.appendChild(heading);
    if (message) card.appendChild(body);
    backdrop.appendChild(card);
    document.body.appendChild(backdrop);
    return { backdrop, card };
  };

  const wireDismiss = (backdrop, onDismiss) => {
    const close = (result) => {
      document.removeEventListener('keydown', onKeydown);
      backdrop.remove();
      onDismiss(result);
    };
    const onKeydown = (evt) => {
      if (evt.key === 'Escape') close(false);
    };
    backdrop.addEventListener('click', (evt) => {
      if (evt.target === backdrop) close(false);
    });
    document.addEventListener('keydown', onKeydown);
    return close;
  };

  const confirm = (options = {}) => new Promise((resolve) => {
    const {
      title = 'Confirm',
      message = '',
      confirmText = 'Yes',
      cancelText = 'No',
      variant = 'sheet'
    } = options;
    const { backdrop, card } = makeDialog({ title, message, variant });
    const actions = document.createElement('div');
    actions.className = 'lnf-dialog-actions';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'lnf-dialog-cancel';
    cancelBtn.textContent = cancelText;
    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'lnf-dialog-confirm';
    confirmBtn.textContent = confirmText;
    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    card.appendChild(actions);
    const close = wireDismiss(backdrop, (result) => resolve(Boolean(result)));
    cancelBtn.addEventListener('click', () => close(false));
    confirmBtn.addEventListener('click', () => close(true));
    cancelBtn.focus();
  });

  const alert = (options = {}) => new Promise((resolve) => {
    const {
      title = 'Notice',
      message = '',
      confirmText = 'OK',
      variant = 'modal'
    } = options;
    const { backdrop, card } = makeDialog({ title, message, variant });
    const actions = document.createElement('div');
    actions.className = 'lnf-dialog-actions';
    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'lnf-dialog-confirm';
    confirmBtn.textContent = confirmText;
    actions.appendChild(confirmBtn);
    card.appendChild(actions);
    const close = wireDismiss(backdrop, () => resolve());
    confirmBtn.addEventListener('click', () => close(true));
    confirmBtn.focus();
  });

  const prompt = (options = {}) => new Promise((resolve) => {
    const {
      title = 'Update',
      message = '',
      label = 'Value',
      placeholder = '',
      value = '',
      confirmText = 'Save',
      cancelText = 'Cancel'
    } = options;
    const { backdrop, card } = makeDialog({ title, message, variant: 'modal' });
    const fieldWrap = document.createElement('label');
    fieldWrap.className = 'lnf-dialog-field';
    const labelEl = document.createElement('span');
    labelEl.textContent = label;
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = placeholder;
    input.value = value;
    fieldWrap.appendChild(labelEl);
    fieldWrap.appendChild(input);
    card.appendChild(fieldWrap);
    const actions = document.createElement('div');
    actions.className = 'lnf-dialog-actions';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = cancelText;
    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'lnf-dialog-confirm';
    confirmBtn.textContent = confirmText;
    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    card.appendChild(actions);
    const close = wireDismiss(backdrop, (ok) => resolve({
      confirmed: Boolean(ok),
      value: Boolean(ok) ? input.value : value
    }));
    cancelBtn.addEventListener('click', () => close(false));
    confirmBtn.addEventListener('click', () => close(true));
    input.focus();
    input.select();
  });

  const form = (options = {}) => new Promise((resolve) => {
    const {
      title = 'Update',
      message = '',
      fields = [],
      confirmText = 'Save',
      cancelText = 'Cancel',
      variant = 'modal'
    } = options;
    const safeFields = Array.isArray(fields) ? fields : [];
    const { backdrop, card } = makeDialog({ title, message, variant });
    const formEl = document.createElement('form');
    formEl.className = 'lnf-dialog-fields';
    const inputs = [];
    safeFields.forEach((field, idx) => {
      const name = String(field?.name || `field_${idx}`);
      const type = String(field?.type || 'text');
      const wrap = document.createElement('label');
      wrap.className = 'lnf-dialog-field';
      const labelEl = document.createElement('span');
      labelEl.textContent = String(field?.label || name);
      const input = type === 'textarea'
        ? document.createElement('textarea')
        : document.createElement('input');
      if (type !== 'textarea') input.type = type;
      input.name = name;
      input.placeholder = String(field?.placeholder || '');
      input.value = String(field?.value || '');
      if (field?.required) input.required = true;
      wrap.appendChild(labelEl);
      wrap.appendChild(input);
      formEl.appendChild(wrap);
      inputs.push({ name, input });
    });
    card.appendChild(formEl);
    const actions = document.createElement('div');
    actions.className = 'lnf-dialog-actions';
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.textContent = cancelText;
    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'submit';
    confirmBtn.className = 'lnf-dialog-confirm';
    confirmBtn.textContent = confirmText;
    actions.appendChild(cancelBtn);
    actions.appendChild(confirmBtn);
    card.appendChild(actions);
    const close = wireDismiss(backdrop, (ok) => {
      if (!ok) {
        resolve({ confirmed: false, values: {} });
        return;
      }
      const values = {};
      inputs.forEach(({ name, input }) => {
        values[name] = input.value;
      });
      resolve({ confirmed: true, values });
    });
    cancelBtn.addEventListener('click', () => close(false));
    formEl.addEventListener('submit', (evt) => {
      evt.preventDefault();
      close(true);
    });
    const first = inputs[0]?.input;
    if (first) {
      first.focus();
      if (typeof first.select === 'function') first.select();
    } else {
      cancelBtn.focus();
    }
  });

  window.lnfUi = {
    toast,
    alert,
    confirm,
    prompt,
    form
  };
})();

document.addEventListener('DOMContentLoaded', () => {
  const DEFAULT_TRADE_BADGE = 'Seedling (beginner)';
  const TRADE_BADGE_POPUP_TEXT = [
    'Trade badge system',
    '',
    'Seedling (beginner)🫘: 0-5 completed trades',
    'Sprout (6-20 trades)🌱: 6-20 completed trades',
    'Producer (21-50 trades)🌿: 21-50 completed trades',
    'Tree (51-100 trades)🌴: 51-100 completed trades',
    'Established Node🌺: 101-250+ completed trades or official node status'
  ].join('\n');

  const uiAlert = (message, title = 'Notice') => window.lnfUi.alert({ title, message });
  const uiConfirm = (message, options = {}) => window.lnfUi.confirm({ message, ...options });
  const uiPrompt = (options = {}) => window.lnfUi.prompt(options);
  const uiForm = (options = {}) => window.lnfUi.form(options);

  const showTradeBadgeInfo = (currentBadge = DEFAULT_TRADE_BADGE) => {
    uiAlert(`${TRADE_BADGE_POPUP_TEXT}\n\nCurrent badge: ${currentBadge}`, 'Trade badge system');
  };
  window.lnfTradeBadgeInfo = showTradeBadgeInfo;

  const showSignInGate = () => {
    window.lnfAuthGate?.show?.();
  };

  const toCounterNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  };

  const extractProfileCounters = (profile = null) => {
    const counters = profile?.profile_counters || {};
    return {
      trades_completed: toCounterNumber(counters.trades_completed ?? profile?.trade_success_count),
      plant_codex_contributions: toCounterNumber(counters.plant_codex_contributions ?? profile?.plant_codex_contributions_count),
      messages: toCounterNumber(counters.messages ?? profile?.messages_count),
      friends_found: toCounterNumber(counters.friends_found ?? profile?.friends_found_count),
      posts: toCounterNumber(counters.posts ?? profile?.posts_count)
    };
  };

  // Footer year
  const yearEl = $('#year');
  if (yearEl) yearEl.textContent = new Date().getFullYear();

  // Explore sidebar (shared across pages)
  const ensureExploreSidebar = () => {
    let sidebar = $('#exploreSidebar');
    let backdrop = $('#exploreSidebarBackdrop');
    if (sidebar && backdrop) return { sidebar, backdrop };

    backdrop = createEl('div', {
      class: 'explore-sidebar-backdrop is-hidden',
      id: 'exploreSidebarBackdrop',
      'aria-hidden': 'true'
    });
    sidebar = createEl('aside', {
      class: 'explore-sidebar is-hidden',
      id: 'exploreSidebar',
      'aria-hidden': 'true',
      'aria-label': 'Explore menu'
    });
    sidebar.innerHTML = `
      <div class="explore-sidebar__header">
        <h2>Explore more with LostNFound</h2>
        <button type="button" class="icon-button" id="closeExploreSidebar" aria-label="Close explore menu">x</button>
      </div>
      <nav class="explore-sidebar__links" aria-label="All pages">
        <a href="index2.html">Home</a>
        <a href="plants.html">Plant Codex</a>
        <a href="trade-space.html">Trade Space</a>
        <a href="profile.html">Profile</a>
        <a href="quote-request.html">Request a Consultation</a>
        <a href="node-system.html">Join the Network</a>
        <a href="stock-register.html">Search Stock &amp; Exchange Registers</a>
        <a href="principles.html">Principles &amp; Guidelines</a>
        <a href="permaculture.html">Permaculture Education</a>
        <a href="tools.html">Borrow Tools for Projects</a>
        <a href="faq.html">Frequently Asked Questions</a>
      </nav>
      <section class="explore-sidebar__about">
        <h3>About LostNFound</h3>
        <p>
          LostNFound Ecosystems is a network of individuals and organizations that trade food, plants, materials, labor, knowledge, and more. The goal is to revive short grass lawns that act as an ecological drain into producing assets that act as ecological beacons.
        </p>
        <p>
          Join the Network by becoming a member in <a href="profile.html">Profile</a>. For questions about sales in Missouri, contact Amy Green <a href="mailto:amygreen@lostnfound.trade">amygreen@lostnfound.trade</a> or <a href="tel:+16362642585">636-264-2585</a>. For questions about business opportunities, joining as a Node, or working for a Node, contact Tyler Graff <a href="mailto:tylergraff@lostnfound.trade">tylergraff@lostnfound.trade</a> or <a href="tel:+16365420501">636-542-0501</a>.
        </p>
      </section>
    `;
    document.body.appendChild(backdrop);
    document.body.appendChild(sidebar);
    return { sidebar, backdrop };
  };

  const ensureSidebarProgressRegion = (sidebar) => {
    if (!sidebar) return null;
    let section = sidebar.querySelector('#sidebarProgressSection');
    if (section) return section;
    section = createEl('section', {
      class: 'explore-sidebar__progress is-hidden',
      id: 'sidebarProgressSection',
      'aria-live': 'polite'
    });
    section.innerHTML = `
      <h3>Your progress</h3>
      <div class="sidebar-progress-grid">
        <p class="sidebar-progress-item"><span>Trades completed</span><strong data-progress-counter="trades_completed">0</strong></p>
        <p class="sidebar-progress-item"><span>Plant Codex contributions</span><strong data-progress-counter="plant_codex_contributions">0</strong></p>
        <p class="sidebar-progress-item"><span>New messages</span><strong data-progress-counter="messages">0</strong></p>
        <p class="sidebar-progress-item"><span>Friends (found)</span><strong data-progress-counter="friends_found">0</strong></p>
        <p class="sidebar-progress-item"><span>Posts</span><strong data-progress-counter="posts">0</strong></p>
      </div>
      <button type="button" class="trade-badge trade-badge--button" id="sidebarTradeBadgeBtn">${DEFAULT_TRADE_BADGE}</button>
    `;
    const aboutSection = sidebar.querySelector('.explore-sidebar__about');
    if (aboutSection && aboutSection.parentNode === sidebar) {
      sidebar.insertBefore(section, aboutSection);
    } else {
      sidebar.appendChild(section);
    }
    return section;
  };

  const ensureSidebarSessionRegion = (sidebar) => {
    if (!sidebar) return null;
    let section = sidebar.querySelector('#sidebarSessionRegion');
    if (section) return section;
    section = createEl('section', {
      class: 'explore-sidebar__session',
      id: 'sidebarSessionRegion',
      'aria-live': 'polite'
    });
    section.innerHTML = `
      <div class="sidebar-session__identity">
        <div class="sidebar-session__avatar" id="sidebarSessionAvatar" aria-hidden="true">
          <span class="sidebar-session__avatar-fallback">U</span>
        </div>
        <div class="sidebar-session__meta">
          <p class="sidebar-session__name" id="sidebarSessionName">Guest</p>
          <p class="sidebar-session__handle" id="sidebarSessionHandle">Not signed in</p>
        </div>
      </div>
      <div class="sidebar-session__actions">
        <button type="button" class="button small" id="sidebarLoginBtn">Login</button>
        <button type="button" class="button ghost small is-hidden" id="sidebarLogoutBtn">Logout</button>
      </div>
    `;
    const header = sidebar.querySelector('.explore-sidebar__header');
    if (header && header.parentNode === sidebar) {
      sidebar.insertBefore(section, header);
    } else {
      sidebar.insertBefore(section, sidebar.firstChild);
    }
    return section;
  };

  const { sidebar: exploreSidebar, backdrop: exploreSidebarBackdrop } = ensureExploreSidebar();
  const sidebarSessionSection = ensureSidebarSessionRegion(exploreSidebar);
  const sidebarProgressSection = ensureSidebarProgressRegion(exploreSidebar);
  const sidebarProgressCounters = sidebarProgressSection
    ? Array.from(sidebarProgressSection.querySelectorAll('[data-progress-counter]'))
    : [];
  const sidebarTradeBadgeBtn = sidebarProgressSection?.querySelector('#sidebarTradeBadgeBtn');
  const sidebarSessionAvatar = sidebarSessionSection?.querySelector('#sidebarSessionAvatar');
  const sidebarSessionName = sidebarSessionSection?.querySelector('#sidebarSessionName');
  const sidebarSessionHandle = sidebarSessionSection?.querySelector('#sidebarSessionHandle');
  const sidebarLoginBtn = sidebarSessionSection?.querySelector('#sidebarLoginBtn');
  const sidebarLogoutBtn = sidebarSessionSection?.querySelector('#sidebarLogoutBtn');
  const closeExploreSidebarBtn = $('#closeExploreSidebar');
  const sidebarToggleButtons = Array.from(document.querySelectorAll('#menuToggleBtn'));

  sidebarToggleButtons.forEach((btn) => {
    btn.setAttribute('aria-controls', 'exploreSidebar');
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Open explore menu');
    btn.querySelectorAll('span').forEach((sp) => {
      if (!sp.classList.contains('sr-only')) sp.remove();
    });
  });

  const setSidebarOpen = (open) => {
    if (!exploreSidebar || !exploreSidebarBackdrop) return;
    exploreSidebar.classList.toggle('is-hidden', !open);
    exploreSidebarBackdrop.classList.toggle('is-hidden', !open);
    exploreSidebar.setAttribute('aria-hidden', open ? 'false' : 'true');
    exploreSidebarBackdrop.setAttribute('aria-hidden', open ? 'false' : 'true');
    sidebarToggleButtons.forEach((btn) => btn.setAttribute('aria-expanded', open ? 'true' : 'false'));
    document.body.classList.toggle('sidebar-open', open);
    if (open) hydrateSidebarProgress();
  };

  if (exploreSidebar && exploreSidebarBackdrop && sidebarToggleButtons.length) {
    sidebarToggleButtons.forEach((btn) => {
      btn.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        const isOpen = !exploreSidebar.classList.contains('is-hidden');
        setSidebarOpen(!isOpen);
      });
    });
    exploreSidebarBackdrop.addEventListener('click', () => setSidebarOpen(false));
    closeExploreSidebarBtn?.addEventListener('click', () => setSidebarOpen(false));
    exploreSidebar.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setSidebarOpen(false));
    });
    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape') setSidebarOpen(false);
    });
  }

  const renderSidebarProgress = (profile = null) => {
    if (!sidebarProgressSection) return;
    const isAuthed = Boolean(localStorage.getItem('lnf_token') && profile?.user_id);
    sidebarProgressSection.classList.toggle('is-hidden', !isAuthed);
    if (!isAuthed) return;
    const counters = extractProfileCounters(profile);
    sidebarProgressCounters.forEach((el) => {
      const key = el.getAttribute('data-progress-counter');
      el.textContent = String(counters[key] || 0);
    });
    if (sidebarTradeBadgeBtn) {
      const badge = String(profile?.trade_badge || DEFAULT_TRADE_BADGE);
      sidebarTradeBadgeBtn.textContent = badge;
      sidebarTradeBadgeBtn.dataset.currentBadge = badge;
    }
  };

  const renderSidebarSession = (profile = null) => {
    if (!sidebarSessionSection) return;
    const isAuthed = Boolean(localStorage.getItem('lnf_token') && profile?.user_id);
    const username = String(profile?.username || '').trim();
    const displayName = String(profile?.display_name || '').trim();
    const preferredName = isAuthed
      ? (displayName || username || 'Community member')
      : 'Guest';
    const handle = isAuthed
      ? (displayName && username ? `@${username}` : 'Signed in')
      : 'Not signed in';
    if (sidebarSessionName) sidebarSessionName.textContent = preferredName;
    if (sidebarSessionHandle) sidebarSessionHandle.textContent = handle;
    if (sidebarLoginBtn) sidebarLoginBtn.classList.toggle('is-hidden', isAuthed);
    if (sidebarLogoutBtn) sidebarLogoutBtn.classList.toggle('is-hidden', !isAuthed);
    if (!sidebarSessionAvatar) return;
    sidebarSessionAvatar.innerHTML = '';
    const avatarUrl = String(profile?.avatar_url || '').trim();
    if (avatarUrl) {
      sidebarSessionAvatar.appendChild(createEl('img', { src: avatarUrl, alt: '', loading: 'lazy' }));
      return;
    }
    const initial = preferredName.charAt(0).toUpperCase() || 'U';
    sidebarSessionAvatar.appendChild(createEl('span', { class: 'sidebar-session__avatar-fallback', text: initial }));
  };

  const hydrateSidebarProgress = async () => {
    const requestedToken = localStorage.getItem('lnf_token');
    if (!localStorage.getItem('lnf_token')) {
      renderSidebarSession(null);
      renderSidebarProgress(null);
      return;
    }
    try {
      const profile = await jsonFetch('/api/profile');
      if (localStorage.getItem('lnf_token') !== requestedToken) return;
      if (!profile?.user_id) {
        renderSidebarSession(null);
        renderSidebarProgress(null);
        return;
      }
      renderSidebarSession(profile);
      renderSidebarProgress(profile);
    } catch (_) {
      renderSidebarSession(null);
      renderSidebarProgress(null);
    }
  };

  sidebarLoginBtn?.addEventListener('click', () => {
    showSignInGate();
  });
  sidebarLogoutBtn?.addEventListener('click', () => {
    clearSession();
    renderSidebarSession(null);
    renderSidebarProgress(null);
  });

  sidebarTradeBadgeBtn?.addEventListener('click', () => {
    const currentBadge = sidebarTradeBadgeBtn.dataset.currentBadge || sidebarTradeBadgeBtn.textContent || DEFAULT_TRADE_BADGE;
    showTradeBadgeInfo(currentBadge);
  });
  hydrateSidebarProgress();
  window.addEventListener('lnf-auth-changed', () => {
    renderSidebarSession(null);
    renderSidebarProgress(null);
  });
  window.addEventListener('lnf:friendship-changed', () => {
    hydrateSidebarProgress().catch(() => {});
  });
  const backToTopBtn = $('#backToTopBtn');
  if (backToTopBtn) {
    const syncBackToTopBtn = () => {
      backToTopBtn.classList.toggle('is-hidden', window.scrollY < 420);
    };
    window.addEventListener('scroll', syncBackToTopBtn, { passive: true });
    backToTopBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    syncBackToTopBtn();
  }

  // Index comments feed
  const commentList = $('#commentList');
  if (commentList) {
    commentList.classList.add('feed-list');
    const TRADE_FEED_MARKER = '[[TRADE_LISTING]]';
    const commentSearchParams = new URLSearchParams(window.location.search);
    const homeFeedControls = $('#homeFeedControls');
    const homeFeedFilterStatus = $('#homeFeedFilterStatus');
    const normalizeFeedMode = (value) => {
      const raw = String(value || '').trim().toLowerCase();
      return raw === 'found' ? 'found' : 'lost';
    };
    let activeFeedMode = normalizeFeedMode(commentSearchParams.get('feed'));
    const coerceGuestFeedModeToLost = () => {
      const signedIn = Boolean(localStorage.getItem('lnf_token'));
      if (signedIn || activeFeedMode !== 'found') return false;
      activeFeedMode = 'lost';
      return true;
    };

    const syncHomeFeedStatus = (explicitMessage = '') => {
      if (!homeFeedFilterStatus) return;
      if (explicitMessage) {
        homeFeedFilterStatus.textContent = explicitMessage;
        return;
      }
      const signedIn = Boolean(localStorage.getItem('lnf_token'));
      if (activeFeedMode === 'found' && !signedIn) {
        homeFeedFilterStatus.textContent = 'Found feed requires sign-in.';
        return;
      }
      const modeLabel = activeFeedMode === 'found'
        ? 'Found feed · connected profiles'
        : 'Lost feed · all profiles';
      homeFeedFilterStatus.textContent = modeLabel;
    };

    const syncHomeFeedControls = () => {
      if (!homeFeedControls) return;
      homeFeedControls.querySelectorAll('[data-feed-mode]').forEach((btn) => {
        const value = normalizeFeedMode(btn.getAttribute('data-feed-mode'));
        const active = value === activeFeedMode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      syncHomeFeedStatus();
    };

    const persistHomeFeedQueryState = () => {
      const url = new URL(window.location.href);
      if (activeFeedMode === 'lost') url.searchParams.delete('feed');
      else url.searchParams.set('feed', activeFeedMode);
      url.searchParams.delete('intent');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };
    if (coerceGuestFeedModeToLost()) {
      persistHomeFeedQueryState();
    }

    const normalizeCommentKey = (value) => {
      const raw = String(value || '').trim();
      if (!raw) return '';
      if (/^\d+$/.test(raw)) return String(Number(raw));
      return raw.toLowerCase();
    };
    const deepLinkPostId = normalizeCommentKey(
      commentSearchParams.get('post_id') || ''
    );
    let deepLinkApplied = false;

    const removeTradeMarkerText = (container) => {
      const candidates = container.querySelectorAll('p, span, div');
      candidates.forEach((el) => {
        if (el.children.length) return;
        const raw = String(el.textContent || '');
        if (!raw.includes(TRADE_FEED_MARKER)) return;
        const cleaned = raw.replaceAll(TRADE_FEED_MARKER, '').replace(/\s{2,}/g, ' ').trim();
        el.textContent = cleaned;
      });
    };

    const normalizeMentionKey = (value) => String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

    const mentionLookup = {
      users: new Map(),
      plants: new Map(),
      userList: [],
      plantList: [],
      ready: false,
      loading: null
    };

    const setMentionLookup = (map, key, payload) => {
      const normalized = normalizeMentionKey(key);
      if (!normalized || map.has(normalized) || !payload) return;
      map.set(normalized, payload);
    };

    const compactMentionToken = (value, fallback = '') => {
      const compact = String(value || '').trim().replace(/[^a-zA-Z0-9_-]+/g, '');
      return compact || String(fallback || '').trim().replace(/[^a-zA-Z0-9_-]+/g, '');
    };

    const pushUniqueMentionOption = (list, payload, key) => {
      const normalized = normalizeMentionKey(key);
      if (!normalized || !payload) return;
      if (list.some((item) => normalizeMentionKey(item?.id || item?.key) === normalized)) return;
      list.push(payload);
    };

    const loadMentionLookup = async () => {
      if (mentionLookup.ready) return mentionLookup;
      if (mentionLookup.loading) return mentionLookup.loading;
      mentionLookup.loading = Promise.all([
        jsonFetch('/api/public-profiles?limit=500').catch(() => []),
        jsonFetch('/api/plants?status=approved').catch(() => [])
      ]).then(([rawUsers, rawPlants]) => {
        const users = Array.isArray(rawUsers) ? rawUsers : [];
        const plants = Array.isArray(rawPlants) ? rawPlants : [];
        users.forEach((user) => {
          const id = String(user?.user_id || '').trim();
          const username = String(user?.username || '').trim();
          const displayName = String(user?.display_name || user?.name || username || 'Community member').trim();
          if (!id) return;
          const mentionText = compactMentionToken(username || displayName, `user${id}`) || `user${id}`;
          const payload = { id, username: username || displayName, displayName, mentionText };
          setMentionLookup(mentionLookup.users, username, payload);
          setMentionLookup(mentionLookup.users, displayName, payload);
          setMentionLookup(mentionLookup.users, mentionText, payload);
          pushUniqueMentionOption(mentionLookup.userList, {
            key: username || displayName || mentionText,
            id,
            username: username || displayName || mentionText,
            displayName,
            mentionText
          }, id);
        });
        plants.forEach((plant) => {
          const id = String(plant?.id || '').trim();
          const commonName = String(plant?.common_name || '').trim();
          const latinName = String(plant?.latin_name || '').trim();
          const label = commonName || latinName || 'Plant';
          const mentionText = compactMentionToken(commonName || latinName || label, `plant${id}`) || `plant${id}`;
          const payload = { id, label, commonName, latinName, mentionText };
          setMentionLookup(mentionLookup.plants, commonName, payload);
          setMentionLookup(mentionLookup.plants, latinName, payload);
          setMentionLookup(mentionLookup.plants, mentionText, payload);
          pushUniqueMentionOption(mentionLookup.plantList, {
            key: commonName || latinName || mentionText,
            id,
            label,
            commonName,
            latinName,
            mentionText
          }, id);
        });
        mentionLookup.userList.sort((a, b) => String(a?.username || a?.displayName || '').localeCompare(String(b?.username || b?.displayName || '')));
        mentionLookup.plantList.sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || '')));
        mentionLookup.ready = true;
        mentionLookup.loading = null;
        return mentionLookup;
      }).catch(() => {
        mentionLookup.loading = null;
        mentionLookup.ready = true;
        return mentionLookup;
      });
      return mentionLookup.loading;
    };

    const mentionQuickPattern = /[@#][a-zA-Z0-9][a-zA-Z0-9_-]{0,48}/;
    const mentionTokenPattern = /(^|[\s([{'"`])([@#])([a-zA-Z0-9][a-zA-Z0-9_-]{0,48})\b/g;

    const buildMentionNode = (symbol, token) => {
      if (symbol === '@') {
        const user = mentionLookup.users.get(normalizeMentionKey(token));
        if (user?.id) {
          const link = createEl('a', {
            class: 'feed-mention feed-mention--forest feed-mention--user',
            href: `profile.html?user=${encodeURIComponent(user.id)}`,
            'data-open-profile-preview': 'true',
            'data-user-id': user.id,
            'data-user-name': user.displayName || user.username || token,
            'aria-label': `View ${user.displayName || user.username || token} profile`
          });
          link.textContent = `@${user.mentionText || user.username || token}`;
          return link;
        }
        return createEl('span', { class: 'feed-mention feed-mention--forest feed-mention--user', text: `@${token}` });
      }

      const plant = mentionLookup.plants.get(normalizeMentionKey(token));
      const plantLabel = plant?.label || token;
      const plantQuery = plantLabel || token;
      const plantHref = `plants.html?q=${encodeURIComponent(plantQuery)}&plant=${encodeURIComponent(plantQuery)}`;
      return createEl('a', {
        class: 'feed-mention feed-mention--forest feed-mention--plant',
        href: plantHref,
        'aria-label': `View ${plantLabel} in Plant Codex`,
        text: `#${token}`
      });
    };

    const mentionDraftPattern = /(^|[\s([{'"`])([@#])([a-zA-Z0-9_-]{0,48})$/;

    const getMentionDraft = (input) => {
      if (!input) return null;
      const value = String(input.value || '');
      const caret = Number(input.selectionStart);
      if (!Number.isFinite(caret) || caret < 0) return null;
      const beforeCaret = value.slice(0, caret);
      const match = beforeCaret.match(mentionDraftPattern);
      if (!match) return null;
      const symbol = match[2] || '';
      const query = match[3] || '';
      return {
        symbol,
        query,
        start: caret - query.length - 1,
        end: caret
      };
    };

    const scoreMentionOption = (symbol, option, normalizedQuery) => {
      const names = symbol === '@'
        ? [option?.username, option?.displayName, option?.mentionText]
        : [option?.label, option?.commonName, option?.latinName, option?.mentionText];
      const normalizedNames = names
        .map((value) => normalizeMentionKey(value))
        .filter(Boolean);
      if (!normalizedNames.length) return Number.POSITIVE_INFINITY;
      if (!normalizedQuery) return 100;
      if (normalizedNames.some((name) => name === normalizedQuery)) return 0;
      if (normalizedNames.some((name) => name.startsWith(normalizedQuery))) return 1;
      if (normalizedNames.some((name) => name.includes(normalizedQuery))) return 2;
      return Number.POSITIVE_INFINITY;
    };

    const getMentionSuggestions = (symbol, query = '') => {
      const normalizedQuery = normalizeMentionKey(query);
      const source = symbol === '@' ? mentionLookup.userList : mentionLookup.plantList;
      return source
        .map((option, index) => ({
          option,
          score: scoreMentionOption(symbol, option, normalizedQuery),
          index
        }))
        .filter((item) => Number.isFinite(item.score))
        .sort((a, b) => a.score - b.score || a.index - b.index)
        .slice(0, 5)
        .map((item) => item.option);
    };

    const getMentionInsertText = (symbol, option) => {
      if (symbol === '@') return `@${option?.mentionText || option?.username || 'user'}`;
      return `#${option?.mentionText || option?.label || 'plant'}`;
    };

    const attachMentionAutocomplete = (input) => {
      if (!input || input.dataset.mentionAutocompleteBound === 'true') return;
      const host = input.parentElement;
      if (!host) return;
      input.dataset.mentionAutocompleteBound = 'true';

      const bar = createEl('div', {
        class: 'mention-suggest is-hidden',
        'aria-hidden': 'true'
      });
      const list = createEl('div', { class: 'mention-suggest__list' });
      bar.appendChild(list);
      host.appendChild(bar);

      let activeDraft = null;

      const hideSuggestions = () => {
        activeDraft = null;
        list.innerHTML = '';
        bar.classList.add('is-hidden');
        bar.setAttribute('aria-hidden', 'true');
      };

      const applySuggestion = (option) => {
        if (!activeDraft) return;
        const value = String(input.value || '');
        const before = value.slice(0, activeDraft.start);
        const after = value.slice(activeDraft.end);
        const insertion = `${getMentionInsertText(activeDraft.symbol, option)} `;
        input.value = `${before}${insertion}${after}`;
        const caret = before.length + insertion.length;
        if (typeof input.setSelectionRange === 'function') {
          input.setSelectionRange(caret, caret);
        }
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        hideSuggestions();
      };

      const renderSuggestions = async () => {
        const draft = getMentionDraft(input);
        if (!draft) {
          hideSuggestions();
          return;
        }
        await loadMentionLookup();
        const suggestions = getMentionSuggestions(draft.symbol, draft.query);
        if (!suggestions.length) {
          hideSuggestions();
          return;
        }
        activeDraft = draft;
        list.innerHTML = '';
        suggestions.forEach((option) => {
          const primary = draft.symbol === '@'
            ? `@${option?.mentionText || option?.username || option?.displayName || 'user'}`
            : `#${option?.mentionText || option?.label || 'plant'}`;
          const secondary = draft.symbol === '@'
            ? String(option?.displayName || '').trim()
            : String(option?.latinName || option?.label || '').trim();
          const button = createEl('button', {
            class: 'mention-suggest__item',
            type: 'button'
          });
          button.appendChild(createEl('span', {
            class: 'mention-suggest__primary',
            text: primary
          }));
          if (secondary && secondary.toLowerCase() !== primary.replace(/^[@#]/, '').toLowerCase()) {
            button.appendChild(createEl('span', {
              class: 'mention-suggest__secondary',
              text: secondary
            }));
          }
          button.addEventListener('mousedown', (evt) => {
            evt.preventDefault();
          });
          button.addEventListener('click', () => applySuggestion(option));
          list.appendChild(button);
        });
        bar.classList.remove('is-hidden');
        bar.setAttribute('aria-hidden', 'false');
      };

      input.addEventListener('focus', () => {
        renderSuggestions().catch(() => hideSuggestions());
      });
      input.addEventListener('input', () => {
        renderSuggestions().catch(() => hideSuggestions());
      });
      input.addEventListener('click', () => {
        renderSuggestions().catch(() => hideSuggestions());
      });
      input.addEventListener('keyup', (evt) => {
        if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight' || evt.key === 'Backspace' || evt.key === 'Delete') {
          renderSuggestions().catch(() => hideSuggestions());
        }
      });
      input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape') hideSuggestions();
        if (evt.key === 'Tab' && !bar.classList.contains('is-hidden')) {
          const first = list.querySelector('.mention-suggest__item');
          if (first) {
            evt.preventDefault();
            first.click();
          }
        }
      });
      input.addEventListener('blur', () => {
        window.setTimeout(hideSuggestions, 120);
      });
    };

    const decorateFeedMentions = () => {
      const nodes = [];
      const walker = document.createTreeWalker(commentList, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const text = String(node?.nodeValue || '');
          if (!text || !mentionQuickPattern.test(text)) return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.closest('.feed-mention, script, style, textarea, input, button, a')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      while (walker.nextNode()) {
        nodes.push(walker.currentNode);
      }
      nodes.forEach((node) => {
        const text = String(node.nodeValue || '');
        mentionTokenPattern.lastIndex = 0;
        let match = null;
        let lastIndex = 0;
        let changed = false;
        const frag = document.createDocumentFragment();
        while ((match = mentionTokenPattern.exec(text)) !== null) {
          const prefix = match[1] || '';
          const symbol = match[2] || '';
          const token = match[3] || '';
          const matchStart = match.index;
          const mentionStart = matchStart + prefix.length;
          const mentionEnd = mentionStart + 1 + token.length;
          if (mentionStart < lastIndex) continue;
          if (matchStart > lastIndex) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex, matchStart)));
          }
          if (prefix) frag.appendChild(document.createTextNode(prefix));
          frag.appendChild(buildMentionNode(symbol, token));
          lastIndex = mentionEnd;
          changed = true;
        }
        if (!changed) return;
        if (lastIndex < text.length) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        node.parentNode?.replaceChild(frag, node);
      });
    };

    const decorateTradeFeedCards = () => {
      const cards = new Set();
      Array.from(commentList.children).forEach((el) => cards.add(el));
      commentList.querySelectorAll('article, li, .feed-item, .comment-card, .comment-item').forEach((el) => cards.add(el));
      cards.forEach((card) => {
        const text = String(card.textContent || '');
        const isTrade = text.includes(TRADE_FEED_MARKER) || /trade listing/i.test(text);
        if (!isTrade) return;
        card.classList.add('trade-feed-highlight');
        removeTradeMarkerText(card);
        if (!card.querySelector('.trade-feed-tag')) {
          const tag = createEl('span', { class: 'trade-feed-tag', text: 'Trade Listing' });
          const anchor = card.querySelector('h2, h3, h4, strong, .comment-topic, .feed-title');
          if (anchor) {
            anchor.parentNode.insertBefore(tag, anchor);
          } else {
            card.insertBefore(tag, card.firstChild);
          }
        }
      });
    };

    let feedDecorateTimer = null;
    const runFeedDecorators = async () => {
      decorateTradeFeedCards();
      const feedText = String(commentList.textContent || '');
      if (!mentionQuickPattern.test(feedText)) return;
      await loadMentionLookup();
      decorateFeedMentions();
    };
    const scheduleFeedDecorators = () => {
      if (feedDecorateTimer) return;
      feedDecorateTimer = window.setTimeout(() => {
        feedDecorateTimer = null;
        runFeedDecorators().catch(() => {});
      }, 0);
    };
    const tradeFeedObserver = new MutationObserver(() => {
      scheduleFeedDecorators();
    });
    tradeFeedObserver.observe(commentList, { childList: true, subtree: true });

    const chatOverlay = $('#chatOverlay');
    const chatThreadList = $('#chatThreadList');
    const chatMessages = $('#chatMessages');
    const chatForm = $('#chatForm');
    const chatInput = $('#chatInput');
    const chatStatus = $('#chatStatus');
    const chatClose = $('#chatClose');
    const viewChatsBtn = $('#viewChatsBtn');
    const chatTitle = $('#chatTitle');
    const chatBackBtn = $('#chatBackBtn');
    const blockUserBtn = $('#blockUserBtn');
    const deleteChatBtn = $('#deleteChatBtn');
    let activeChatUserId = null;
    const setChatUnreadBadge = (count = 0) => {
      if (!viewChatsBtn) return;
      const safeCount = Number.isFinite(Number(count)) ? Math.max(0, Math.floor(Number(count))) : 0;
      let badgeEl = viewChatsBtn.querySelector('.chat-unread-badge');
      if (!safeCount) {
        if (badgeEl) badgeEl.remove();
        viewChatsBtn.classList.remove('has-unread');
        viewChatsBtn.setAttribute('aria-label', 'Chat');
        return;
      }
      if (!badgeEl) {
        badgeEl = createEl('span', {
          class: 'chat-unread-badge',
          'aria-live': 'polite'
        });
        viewChatsBtn.appendChild(badgeEl);
      }
      const label = safeCount > 99 ? '99+' : String(safeCount);
      badgeEl.textContent = label;
      viewChatsBtn.classList.add('has-unread');
      viewChatsBtn.setAttribute('aria-label', `Chat (${safeCount} unread)`);
    };
    const buildChatEmptyState = (title, hint = '') => {
      const wrap = createEl('div', { class: 'chat-empty-state' });
      wrap.appendChild(createEl('p', { class: 'chat-empty-state__title', text: title }));
      if (hint) {
        wrap.appendChild(createEl('p', { class: 'chat-empty-state__hint muted small', text: hint }));
      }
      return wrap;
    };

    const initialFor = (name) => {
      const first = String(name || '').trim().charAt(0);
      return first ? first.toUpperCase() : 'U';
    };

    const renderAvatar = (el, src, name = '') => {
      if (!el) return;
      el.innerHTML = '';
      if (src) {
        const img = createEl('img', { src, alt: `${name || 'Profile'} photo` });
        el.appendChild(img);
        return;
      }
      el.appendChild(createEl('span', { class: 'feed-avatar__fallback', text: initialFor(name) }));
    };

    const trimText = (value) => String(value || '').trim();
    const toRows = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.messages)) return payload.messages;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.items)) return payload.items;
      if (Array.isArray(payload?.threads)) return payload.threads;
      return [];
    };
    const formatStamp = (value) => {
      if (!value) return '';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
    };
    const threadUserId = (row) => String(row?.user_id || '').trim();
    const threadName = (row) => trimText(row?.display_name || row?.name || row?.username || 'Community member') || 'Community member';
    const threadAvatar = (row) => trimText(row?.avatar_url || '');
    const getErrorMessage = (err, fallback = 'Something went wrong.') => {
      const raw = trimText(err?.message || '');
      if (!raw) return fallback;
      try {
        const parsed = JSON.parse(raw);
        const msg = trimText(parsed?.error || parsed?.message || '');
        if (msg) return msg;
      } catch (_) {
        // non-json error text
      }
      return raw;
    };

    let viewerUserId = '';
    let viewerIsAdmin = false;
    let blockedUserIds = new Set();
    let viewerContextToken = '';
    let viewerContextPromise = null;

    const setBlockedUsersFromRows = (rows = []) => {
      const next = new Set();
      rows.forEach((row) => {
        const blockedId = trimText(row?.blocked_id || row?.blockedId || row?.user_id || row?.userId || '');
        if (blockedId) next.add(blockedId);
      });
      blockedUserIds = next;
    };

    const isSelfUserId = (userId) => {
      const candidate = trimText(userId);
      return Boolean(candidate && viewerUserId && candidate === viewerUserId);
    };

    const isBlockedUserId = (userId) => {
      const candidate = trimText(userId);
      return Boolean(candidate && blockedUserIds.has(candidate));
    };

    let chatViewMode = 'inbox';
    const setChatView = (mode = 'inbox') => {
      chatViewMode = mode === 'thread' ? 'thread' : 'inbox';
      if (chatOverlay) chatOverlay.setAttribute('data-chat-view', chatViewMode);
      syncChatActionButtons();
    };
    const syncChatActionButtons = () => {
      const inThreadView = chatViewMode === 'thread';
      const hasActive = Boolean(trimText(activeChatUserId));
      if (blockUserBtn) {
        const blocked = hasActive ? isBlockedUserId(activeChatUserId) : false;
        blockUserBtn.classList.toggle('is-hidden', !inThreadView);
        blockUserBtn.disabled = !inThreadView || !hasActive;
        blockUserBtn.textContent = blocked ? 'Unblock' : 'Block';
      }
      if (deleteChatBtn) {
        deleteChatBtn.classList.toggle('is-hidden', !inThreadView);
        deleteChatBtn.disabled = !inThreadView || !hasActive;
      }
      if (chatBackBtn) chatBackBtn.classList.toggle('is-hidden', !inThreadView);
    };

    const loadViewerContext = async ({ force = false } = {}) => {
      const token = String(localStorage.getItem('lnf_token') || '');
      if (!token) {
        viewerContextToken = '';
        viewerUserId = '';
        viewerIsAdmin = false;
        blockedUserIds = new Set();
        syncChatActionButtons();
        return;
      }
      if (!force && viewerContextToken === token && viewerContextPromise) {
        await viewerContextPromise;
        return;
      }
      if (!force && viewerContextToken === token && viewerUserId) {
        return;
      }

      viewerContextToken = token;
      viewerContextPromise = (async () => {
        try {
          const me = await jsonFetch('/api/auth/me');
          if (localStorage.getItem('lnf_token') !== token) return;
          viewerUserId = trimText(me?.id || '');
          viewerIsAdmin = Boolean(me?.is_admin && (me.is_primary_admin || (me.admin_stations || []).includes('trades')));
        } catch (_) {
          viewerUserId = '';
          viewerIsAdmin = false;
        }
        try {
          const rows = await jsonFetch('/api/blocks');
          setBlockedUsersFromRows(Array.isArray(rows) ? rows : []);
        } catch (_) {
          blockedUserIds = new Set();
        }
      })();

      try {
        await viewerContextPromise;
      } finally {
        viewerContextPromise = null;
        syncChatActionButtons();
      }
    };

    const toggleBlockUser = async (targetUserId) => {
      const target = trimText(targetUserId);
      if (!target) throw new Error('Could not determine user to block.');
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        throw new Error('Sign in required.');
      }

      await loadViewerContext();
      if (!viewerUserId) throw new Error('Sign in required.');
      if (target === viewerUserId) throw new Error('You cannot block yourself.');

      const numericTarget = Number(target);
      if (!Number.isFinite(numericTarget)) throw new Error('Invalid user id.');

      const blocked = isBlockedUserId(target);
      if (blocked) {
        await jsonFetch(`/api/blocks/${encodeURIComponent(target)}`, { method: 'DELETE' });
      } else {
        await jsonFetch('/api/blocks', {
          method: 'POST',
          body: JSON.stringify({ user_id: numericTarget })
        });
      }

      await loadViewerContext({ force: true });
      return !blocked;
    };

    let activeChatName = '';
    let activeChatAvatar = '';

    const closeChatOverlay = () => {
      if (!chatOverlay) return;
      chatOverlay.classList.add('is-hidden');
      setChatView('inbox');
      activeChatUserId = null;
      activeChatName = '';
      activeChatAvatar = '';
      if (chatStatus) chatStatus.textContent = '';
      if (chatMessages) chatMessages.innerHTML = '';
      if (chatInput) chatInput.value = '';
      syncChatActionButtons();
    };
    const openChatOverlay = () => {
      if (!chatOverlay) return false;
      chatOverlay.classList.remove('is-hidden');
      if (!chatOverlay.getAttribute('data-chat-view')) setChatView('inbox');
      syncChatActionButtons();
      return true;
    };

    const renderChatMessages = (rows = []) => {
      if (!chatMessages) return;
      chatMessages.innerHTML = '';
      if (!rows.length) {
        chatMessages.appendChild(buildChatEmptyState(
          'No messages yet.',
          activeChatUserId
            ? 'Start with a simple hello to begin this chat.'
            : 'Select a conversation to view messages.'
        ));
        return;
      }
      rows.slice(-120).forEach((row) => {
        const fromMe = Boolean(row?.is_mine);
        const body = trimText(row?.body || '');
        const imageUrl = trimText(row?.image_url || '');
        const stamp = formatStamp(row?.created_at || null);

        const wrap = createEl('div', { class: `chat-message ${fromMe ? 'is-mine' : 'is-theirs'}` });
        const bubble = createEl('div', { class: 'chat-bubble' });
        if (body) bubble.appendChild(createEl('p', { text: body }));
        if (imageUrl) bubble.appendChild(createEl('img', { src: imageUrl, alt: 'Attached photo', loading: 'lazy' }));
        if (!body && !imageUrl) bubble.appendChild(createEl('p', { text: 'No message text.' }));
        wrap.appendChild(bubble);
        if (stamp) wrap.appendChild(createEl('span', { class: 'chat-time', text: stamp }));
        chatMessages.appendChild(wrap);
      });
      chatMessages.scrollTop = chatMessages.scrollHeight;
    };

    const renderChatThreadList = (threads = []) => {
      if (!chatThreadList) return;
      chatThreadList.innerHTML = '';
      if (!threads.length) {
        chatThreadList.appendChild(buildChatEmptyState(
          'No conversations yet.',
          'Open any public profile and press Message to start a chat.'
        ));
        setChatUnreadBadge(0);
        return;
      }
      const activeId = String(activeChatUserId || '').trim();
      let totalUnread = 0;
      threads.forEach((thread) => {
        const userId = threadUserId(thread);
        if (!userId) return;
        const name = threadName(thread);
        const avatar = threadAvatar(thread);
        const preview = trimText(thread?.last_message) || (trimText(thread?.image_url) ? 'Sent a photo.' : '');
        const stamp = formatStamp(thread?.last_timestamp || null);
        const unreadRaw = Number(thread?.unread_count ?? 0);
        const unreadCount = Number.isFinite(unreadRaw) && unreadRaw > 0 ? Math.floor(unreadRaw) : 0;
        if (unreadCount > 0) totalUnread += unreadCount;

        const item = createEl('button', { class: 'chat-thread', type: 'button' });
        item.dataset.userId = userId;
        item.dataset.name = name;
        item.dataset.avatar = avatar;
        if (activeId && activeId === userId) item.classList.add('is-active');

        const avatarEl = createEl('div', { class: 'avatar avatar--sm', 'aria-hidden': 'true' });
        renderAvatar(avatarEl, avatar || null, name);

        const bodyEl = createEl('div', { class: 'chat-thread__body' });
        const nameRow = createEl('div', { class: 'chat-thread__name-row' });
        nameRow.appendChild(createEl('p', { class: 'chat-thread__name', text: name }));
        if (unreadCount > 0) {
          nameRow.appendChild(createEl('span', {
            class: 'chat-thread__unread',
            text: unreadCount > 99 ? '99+' : String(unreadCount)
          }));
        }
        bodyEl.appendChild(nameRow);
        bodyEl.appendChild(createEl('p', { class: 'chat-thread__preview', text: preview }));
        bodyEl.appendChild(createEl('span', { class: 'chat-thread__time', text: stamp }));

        item.appendChild(avatarEl);
        item.appendChild(bodyEl);
        item.addEventListener('click', () => {
          openChatWithUser(userId, name, avatar);
        });
        chatThreadList.appendChild(item);
      });
      setChatUnreadBadge(totalUnread);
    };

    const withPreferredThread = (threads, preferred = null) => {
      const userId = trimText(preferred?.userId || '');
      if (!userId) return threads;
      const existing = threads.find((thread) => threadUserId(thread) === userId);
      if (existing) {
        if (!trimText(existing.display_name) && trimText(preferred?.name)) existing.display_name = preferred.name;
        if (!trimText(existing.avatar_url) && trimText(preferred?.avatar)) existing.avatar_url = preferred.avatar;
        return threads;
      }
      return [
        {
          user_id: userId,
          display_name: trimText(preferred?.name || `User ${userId}`),
          avatar_url: trimText(preferred?.avatar || ''),
          last_message: '',
          last_timestamp: null,
          unread_count: 0
        },
        ...threads
      ];
    };

    const loadChatThreads = async (preferred = null) => {
      try {
        const threads = withPreferredThread(toRows(await jsonFetch('/api/messages')), preferred);
        renderChatThreadList(threads);
        return threads;
      } catch (_) {
        const fallbackThreads = withPreferredThread([], preferred);
        renderChatThreadList(fallbackThreads);
        if (!fallbackThreads.length && chatStatus) chatStatus.textContent = 'Could not load conversations.';
        return fallbackThreads;
      }
    };
    const refreshChatUnreadBadge = async () => {
      if (!localStorage.getItem('lnf_token')) {
        setChatUnreadBadge(0);
        return;
      }
      try {
        const threads = toRows(await jsonFetch('/api/messages'));
        const totalUnread = threads.reduce((sum, row) => {
          const value = Number(row?.unread_count ?? row?.unreadCount ?? 0);
          if (!Number.isFinite(value) || value <= 0) return sum;
          return sum + Math.floor(value);
        }, 0);
        setChatUnreadBadge(totalUnread);
      } catch (_) {
        setChatUnreadBadge(0);
      }
    };

    const loadChatConversation = async (userId) => {
      const id = trimText(userId);
      if (!id) {
        renderChatMessages([]);
        if (chatStatus) chatStatus.textContent = 'Select a conversation to view messages.';
        return [];
      }
      if (chatStatus) chatStatus.textContent = 'Loading conversation...';
      try {
        const rows = toRows(await jsonFetch(`/api/messages/${encodeURIComponent(id)}`));
        renderChatMessages(rows);
        if (chatStatus) chatStatus.textContent = rows.length ? '' : 'No messages yet. Say hello!';
        return rows;
      } catch (_) {
        renderChatMessages([]);
        if (chatStatus) chatStatus.textContent = 'Could not load conversation.';
        return [];
      }
    };

    const openChatWithUser = async (userId, name = 'Community member', avatar = '') => {
      const id = trimText(userId);
      if (!id) return;
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        if (chatStatus) chatStatus.textContent = 'Sign in to open chats.';
        return;
      }
      if (!openChatOverlay()) return;
      await loadViewerContext();
      activeChatUserId = id;
      activeChatName = trimText(name) || 'Community member';
      activeChatAvatar = trimText(avatar);
      setChatView('thread');
      syncChatActionButtons();
      if (chatTitle) chatTitle.textContent = `Chat with ${activeChatName}`;
      await loadChatThreads({ userId: id, name: activeChatName, avatar: activeChatAvatar });
      await loadChatConversation(id);
      await loadChatThreads({ userId: id, name: activeChatName, avatar: activeChatAvatar });
      chatInput?.focus();
    };

    const openChatInbox = async (preferred = null) => {
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        if (chatStatus) chatStatus.textContent = 'Sign in to open chats.';
        return;
      }
      if (!openChatOverlay()) return;
      await loadViewerContext();
      const preferredUserId = trimText(preferred?.userId || '');
      if (preferredUserId) {
        activeChatUserId = preferredUserId;
        activeChatName = trimText(preferred?.name || activeChatName || 'Community member');
        activeChatAvatar = trimText(preferred?.avatar || activeChatAvatar || '');
      }
      setChatView('inbox');
      if (chatTitle) chatTitle.textContent = 'Messages';
      if (chatStatus) chatStatus.textContent = 'Loading conversations...';
      const threads = await loadChatThreads(preferred);
      if (!threads.length) {
        activeChatUserId = null;
        activeChatName = '';
        activeChatAvatar = '';
        syncChatActionButtons();
        renderChatMessages([]);
        if (chatStatus) chatStatus.textContent = 'No conversations yet. Open any public profile and press Message.';
        return;
      }
      renderChatThreadList(threads);
      if (chatStatus) chatStatus.textContent = 'Select a conversation to open chat.';
    };
    window.lnfSharedChat = {
      openInbox: openChatInbox
    };
    window.lnfSharedChatState = {
      loadViewerContext,
      isSelfUserId,
      isBlockedUserId,
      toggleBlockUser,
      getErrorMessage
    };

    chatClose?.addEventListener('click', closeChatOverlay);
    viewChatsBtn?.addEventListener('click', openChatInbox);
    window.addEventListener('lnf:session-cleared', () => {
      viewerUserId = '';
      viewerIsAdmin = false;
      viewerContextToken = '';
      viewerContextPromise = null;
      closeChatOverlay();
      setChatUnreadBadge(0);
    });
    window.addEventListener('focus', () => {
      refreshChatUnreadBadge().catch(() => {});
    });
    refreshChatUnreadBadge().catch(() => {});
    chatBackBtn?.addEventListener('click', () => {
      openChatInbox();
    });
    chatOverlay?.addEventListener('click', (evt) => {
      if (evt.target === chatOverlay) closeChatOverlay();
    });
    blockUserBtn?.addEventListener('click', async () => {
      const target = trimText(activeChatUserId);
      if (!target) return;
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        if (chatStatus) chatStatus.textContent = 'Sign in required.';
        return;
      }
      const currentlyBlocked = isBlockedUserId(target);
      const question = currentlyBlocked
        ? 'Unblock this user so messages can resume?'
        : 'Block this user? You will no longer be able to message each other.';
      if (!await uiConfirm(question, { title: currentlyBlocked ? 'Unblock user' : 'Block user' })) return;

      if (chatStatus) chatStatus.textContent = currentlyBlocked ? 'Unblocking...' : 'Blocking...';
      blockUserBtn.disabled = true;
      try {
        const isNowBlocked = await toggleBlockUser(target);
        if (chatStatus) chatStatus.textContent = isNowBlocked ? 'User blocked.' : 'User unblocked.';
        if (isNowBlocked) {
          renderChatMessages([]);
        } else {
          await loadChatConversation(target);
        }
      } catch (err) {
        if (chatStatus) chatStatus.textContent = getErrorMessage(err, 'Could not update block.');
      } finally {
        syncChatActionButtons();
      }
    });
    deleteChatBtn?.addEventListener('click', async () => {
      const target = trimText(activeChatUserId);
      if (!target) return;
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        if (chatStatus) chatStatus.textContent = 'Sign in required.';
        return;
      }
      if (!await uiConfirm('Delete this conversation?', { title: 'Delete conversation', confirmText: 'Delete' })) return;

      if (chatStatus) chatStatus.textContent = 'Deleting conversation...';
      deleteChatBtn.disabled = true;
      try {
        await jsonFetch(`/api/messages/${encodeURIComponent(target)}`, { method: 'DELETE' });
        activeChatUserId = null;
        activeChatName = '';
        activeChatAvatar = '';
        setChatView('inbox');
        renderChatMessages([]);
        await loadChatThreads();
        if (chatTitle) chatTitle.textContent = 'Messages';
        if (chatStatus) chatStatus.textContent = 'Conversation deleted.';
      } catch (err) {
        if (chatStatus) chatStatus.textContent = getErrorMessage(err, 'Could not delete conversation.');
      } finally {
        syncChatActionButtons();
      }
    });
    setChatView('inbox');

    chatForm?.addEventListener('submit', async (evt) => {
      evt.preventDefault();
      const body = trimText(chatInput?.value || '');
      if (!activeChatUserId || !body) return;
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        if (chatStatus) chatStatus.textContent = 'Sign in required.';
        return;
      }
      if (chatStatus) chatStatus.textContent = 'Sending...';
      try {
        await jsonFetch('/api/messages', {
          method: 'POST',
          body: JSON.stringify({ to: Number(activeChatUserId), body })
        });
        if (chatInput) chatInput.value = '';
        const threads = await loadChatThreads({
          userId: activeChatUserId,
          name: activeChatName,
          avatar: activeChatAvatar
        });
        renderChatThreadList(threads);
        await loadChatConversation(activeChatUserId);
      } catch (_) {
        if (chatStatus) chatStatus.textContent = 'Could not send message.';
      }
    });

    const buildCommentFeedEndpoint = () => {
      const params = new URLSearchParams();
      params.set('limit', deepLinkPostId ? '200' : '50');
      params.set('feed_scope', activeFeedMode === 'found' ? 'friends' : 'global');
      return `/api/comments?${params.toString()}`;
    };

    const asArray = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (payload && Array.isArray(payload.comments)) return payload.comments;
      if (payload && Array.isArray(payload.data)) return payload.data;
      if (payload && Array.isArray(payload.results)) return payload.results;
      if (payload && Array.isArray(payload.items)) return payload.items;
      return [];
    };

    const text = (value) => String(value || '').trim();
    const parseStringArray = (value) => {
      if (Array.isArray(value)) {
        return value.map((item) => text(item)).filter(Boolean);
      }
      const raw = text(value);
      if (!raw) return [];
      if (raw.startsWith('[') && raw.endsWith(']')) {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed.map((item) => text(item)).filter(Boolean) : [];
        } catch (_) {
          return [];
        }
      }
      return [raw];
    };
    const stripTradeFeedPrefix = (value) => text(value).replace(/^trade listing:\s*/i, '').trim();
    const inferFeedMediaType = (row, mediaData, mediaTypeRaw) => {
      const explicit = text(mediaTypeRaw).toLowerCase();
      if (explicit === 'video' || explicit === 'photo') return explicit;
      if (typeof mediaData === 'string' && mediaData.startsWith('data:video/')) return 'video';
      if (typeof mediaData === 'string' && mediaData.startsWith('data:image/')) return 'photo';
      const mediaPath = text(mediaData).toLowerCase();
      if (/\.(mp4|webm|mov|m4v|ogg|avi|mpeg)(\?|#|$)/i.test(mediaPath)) return 'video';
      if (/\.(jpg|jpeg|png|gif|webp|avif|bmp|svg)(\?|#|$)/i.test(mediaPath)) return 'photo';
      const topicText = text(row?.topic || row?.title).toLowerCase();
      if (topicText.startsWith('[video]')) return 'video';
      if (topicText.startsWith('[photo]')) return 'photo';
      return mediaPath ? 'photo' : '';
    };
    const inferFeedVideoMimeType = (row, mediaData) => {
      const explicitMime = text(row?.mime_type).toLowerCase();
      if (explicitMime.startsWith('video/')) return explicitMime.split(';')[0].trim();
      if (typeof mediaData === 'string' && mediaData.startsWith('data:video/')) {
        const endIndex = mediaData.indexOf(';');
        if (endIndex > 5) return mediaData.slice(5, endIndex).toLowerCase();
      }
      const mediaPath = text(mediaData).toLowerCase();
      if (/\.(mp4|m4v)(\?|#|$)/i.test(mediaPath)) return 'video/mp4';
      if (/\.webm(\?|#|$)/i.test(mediaPath)) return 'video/webm';
      if (/\.mov(\?|#|$)/i.test(mediaPath)) return 'video/quicktime';
      if (/\.ogg(\?|#|$)/i.test(mediaPath)) return 'video/ogg';
      if (/\.avi(\?|#|$)/i.test(mediaPath)) return 'video/x-msvideo';
      if (/\.mpeg(\?|#|$)/i.test(mediaPath)) return 'video/mpeg';
      return '';
    };
    const canBrowserPlayVideoMime = (mimeType) => {
      const normalized = text(mimeType).toLowerCase();
      if (!normalized) return true;
      try {
        const probe = document.createElement('video');
        const support = typeof probe.canPlayType === 'function' ? probe.canPlayType(normalized) : '';
        return Boolean(support && support !== 'no');
      } catch (_err) {
        return true;
      }
    };
    const renderFeedVideoFallback = (mediaFrame, mediaUrl) => {
      if (!mediaFrame) return;
      mediaFrame.innerHTML = '';
      const link = createEl('a', {
        class: 'button secondary small',
        href: mediaUrl || '#',
        target: '_blank',
        rel: 'noopener noreferrer',
        text: 'Open video'
      });
      link.setAttribute('aria-label', 'Open video in a new tab');
      mediaFrame.appendChild(link);
    };
    const openTradeListing = (tradeId) => {
      const id = text(tradeId);
      if (!id) return;
      const url = new URL('trade-space.html', window.location.href);
      url.searchParams.set('trade_id', id);
      window.location.href = url.toString();
    };
    const openSharedProfilePreview = (userId, fallbackName = 'Community member') => {
      const openProfile = window.lnfGlobalSearch?.openProfilePreview;
      if (typeof openProfile === 'function') {
        openProfile(userId, fallbackName);
        return;
      }
      if (userId) {
        window.location.href = `profile.html?user=${encodeURIComponent(userId)}`;
        return;
      }
      window.location.href = 'profile.html';
    };
    const openCommentModalBtn = $('#openCommentModal');
    const commentModal = $('#commentModal');
    const commentForm = $('#commentForm');
    const commentTopic = $('#commentTopic');
    const commentBody = $('#commentBody');
    const commentBodyLabel = $('#commentBodyLabel');
    const commentStatus = $('#commentStatus');
    const commentModalTitle = $('#commentModalTitle');
    const commentSubmitBtn = $('#commentSubmitBtn');
    const commentPostType = $('#commentPostType');
    const postTypeToggle = $('#postTypeToggle');
    const postTypeButtons = Array.from(postTypeToggle?.querySelectorAll('[data-post-type]') || []);
    const postIdeaSelect = $('#postIdeaSelect');
    const postMediaRow = $('#postMediaRow');
    const postMediaLabel = $('#postMediaLabel');
    const postMediaHelp = $('#postMediaHelp');
    const postMediaFile = $('#postMediaFile');
    const postMediaPicker = $('#postMediaPicker');
    const postMediaPickerText = $('#postMediaPickerText');
    const postMediaPreview = $('#postMediaPreview');
    attachMentionAutocomplete(commentTopic);
    attachMentionAutocomplete(commentBody);

    const POST_TYPE_CONFIG = {
      comment: {
        modalTitle: 'Create a Comment Post',
        bodyLabel: 'Comment',
        bodyPlaceholder: 'Share your story, question, or tip.',
        submitLabel: 'Post comment',
        mediaLabel: '',
        mediaHelp: '',
        mediaAccept: '',
        pickerLabel: 'Add media',
        requiresMedia: false
      },
      photo: {
        modalTitle: 'Create a Photo Post',
        bodyLabel: 'Caption',
        bodyPlaceholder: 'Describe what is in this photo and why it matters.',
        submitLabel: 'Post photo',
        mediaLabel: 'Upload Photo',
        mediaHelp: 'Choose a photo file (max 12 MB). Large photos are auto-optimized.',
        mediaAccept: 'image/*',
        pickerLabel: 'Add photo',
        requiresMedia: true
      },
      video: {
        modalTitle: 'Create a Video Post',
        bodyLabel: 'Description',
        bodyPlaceholder: 'Share context so people know what to watch for.',
        submitLabel: 'Post video',
        mediaLabel: 'Upload Video',
        mediaHelp: 'Choose a video file (max 80 MB). Large videos are auto-optimized when possible.',
        mediaAccept: 'video/*',
        pickerLabel: 'Add video',
        requiresMedia: true
      }
    };
    const POST_PROMPTS = [
      'What did I learn from my garden this week?',
      'Show your latest harvest and what you made with it.',
      'What native plant surprised you this season?',
      'What is one thing you would trade today and why?',
      'Before and after: project update from my yard.',
      'What challenge are you stuck on right now?',
      'What tool saved you the most time this month?',
      'How did you find this community?',
      'What would you plant in a small shaded spot?',
      'What seed are you most excited to share?',
      'What is your favorite pollinator-friendly plant?',
      'Ask the community for help with one specific issue.',
      'Share one photo of progress and one next step.',
      'What did you lose and hope to find this week?',
      'What did you find and want to share forward?',
      'What is your best low-budget garden tip?',
      'What plant failed and what did it teach you?',
      'What compost habit improved your soil fastest?',
      'What local weather shift changed your plans?',
      'What is your current trade goal for this month?',
      'What is one plant myth you want to challenge?',
      'What is your favorite part of your neighborhood ecosystem?',
      'Show your workspace setup for planting or trade prep.',
      'What community win are you celebrating today?'
    ];

	    let activePostType = 'comment';
	    let previewObjectUrl = '';
	    const POST_MEDIA_LIMITS = {
	      photo: 12 * 1024 * 1024,
	      video: 80 * 1024 * 1024
	    };
	    let selectedSourceMediaKey = '';
	    let editedPostMediaFile = null;
	    let mediaEditorOverlay = null;
	    let mediaEditorControls = null;
	    let mediaEditorTabBtn = null;
	    let mediaEditorBackBtn = null;
	    let mediaEditorApplyBtn = null;
	    let mediaEditorStatus = null;
	    let mediaEditorImageEl = null;
	    let mediaEditorVideoEl = null;
	    let mediaEditorVideoOnlyControls = [];
	    let mediaEditorBrightnessInput = null;
	    let mediaEditorContrastInput = null;
	    let mediaEditorSaturationInput = null;
	    let mediaEditorRotateBtn = null;
	    let mediaEditorFlipBtn = null;
	    let mediaEditorResetBtn = null;
	    let mediaEditorTextInput = null;
	    let mediaEditorTextColorInput = null;
	    let mediaEditorTextSizeInput = null;
	    let mediaEditorTextXInput = null;
	    let mediaEditorTextYInput = null;
	    let mediaEditorTrimStartInput = null;
	    let mediaEditorTrimEndInput = null;
	    let mediaEditorTrimStartValue = null;
	    let mediaEditorTrimEndValue = null;
	    let mediaEditorTextOverlay = null;
	    let mediaEditorObjectUrl = '';
	    let mediaEditorSourceFile = null;
	    let mediaEditorSourceType = '';
	    let mediaEditorControlsOpen = false;
	    let photoEditorState = {
	      brightness: 100,
	      contrast: 100,
	      saturation: 100,
	      rotate: 0,
	      flipX: false
	    };
	    let videoEditorState = {
	      start: 0,
	      end: 0,
	      duration: 0
	    };
	    let textOverlayState = {
	      text: '',
	      color: '#ffffff',
	      size: 44,
	      x: 50,
	      y: 86
	    };
	    let closeMediaEditor = () => {};
	    let openMediaEditor = () => {};

	    const getFileKey = (file) => {
	      if (!file) return '';
	      return [file.name, file.size, file.lastModified, file.type].join('|');
	    };

	    const getSelectedMediaFile = () => editedPostMediaFile || postMediaFile?.files?.[0] || null;

	    const clearMediaPreview = () => {
	      if (previewObjectUrl) {
	        URL.revokeObjectURL(previewObjectUrl);
	        previewObjectUrl = '';
      }
      if (!postMediaPreview) return;
      postMediaPreview.innerHTML = '';
      postMediaPreview.classList.add('is-hidden');
    };

	    const renderMediaPreview = (file, type) => {
	      clearMediaPreview();
	      if (!postMediaPreview || !file) return;
	      previewObjectUrl = URL.createObjectURL(file);
	      const isVideo = type === 'video' || String(file.type || '').startsWith('video/');
	      const mediaEl = isVideo
	        ? createEl('video', { class: 'post-media-preview__video', src: previewObjectUrl, muted: 'muted', playsinline: 'playsinline', preload: 'metadata' })
	        : createEl('img', { class: 'post-media-preview__thumb', src: previewObjectUrl, alt: 'Selected upload preview' });
	      mediaEl.classList.add('post-media-preview__interactive');
	      mediaEl.setAttribute('role', 'button');
	      mediaEl.setAttribute('tabindex', '0');
	      mediaEl.setAttribute('aria-label', 'Open full screen media editor');
	      mediaEl.addEventListener('click', () => openMediaEditor(file, type));
	      mediaEl.addEventListener('keydown', (evt) => {
	        if (evt.key !== 'Enter' && evt.key !== ' ') return;
	        evt.preventDefault();
	        openMediaEditor(file, type);
	      });
	      const editedSuffix = editedPostMediaFile && file === editedPostMediaFile ? ' (edited)' : '';
	      const fileLabel = file.name || (isVideo ? 'Selected video' : 'Selected photo');
	      const nameEl = createEl('p', { class: 'post-media-preview__name', text: `${fileLabel}${editedSuffix}` });
	      const hintEl = createEl('p', {
	        class: 'post-media-preview__hint muted small',
	        text: 'Click media to open full-screen editor.'
	      });
	      postMediaPreview.appendChild(mediaEl);
	      postMediaPreview.appendChild(nameEl);
	      postMediaPreview.appendChild(hintEl);
	      postMediaPreview.classList.remove('is-hidden');
	    };

	    const syncSelectedMediaPreview = () => {
	      const sourceFile = postMediaFile?.files?.[0] || null;
	      const sourceKey = getFileKey(sourceFile);
	      if (!sourceFile) {
	        selectedSourceMediaKey = '';
	        editedPostMediaFile = null;
	        clearMediaPreview();
	        return;
	      }
	      if (sourceKey !== selectedSourceMediaKey) {
	        selectedSourceMediaKey = sourceKey;
	        editedPostMediaFile = null;
	      }
	      const file = editedPostMediaFile || sourceFile;
	      if (activePostType === 'photo' && !String(file.type || '').startsWith('image/')) {
	        if (commentStatus) commentStatus.textContent = 'Please choose an image file for a photo post.';
	        if (postMediaFile) postMediaFile.value = '';
	        selectedSourceMediaKey = '';
	        editedPostMediaFile = null;
	        clearMediaPreview();
	        return;
	      }
	      if (activePostType === 'video' && !String(file.type || '').startsWith('video/')) {
	        if (commentStatus) commentStatus.textContent = 'Please choose a video file for a video post.';
	        if (postMediaFile) postMediaFile.value = '';
	        selectedSourceMediaKey = '';
	        editedPostMediaFile = null;
	        clearMediaPreview();
	        return;
	      }
	      if (commentStatus && commentStatus.textContent) commentStatus.textContent = '';
	      renderMediaPreview(file, activePostType);
    };

    const applyPostType = (nextType) => {
      const type = Object.prototype.hasOwnProperty.call(POST_TYPE_CONFIG, nextType) ? nextType : 'comment';
      activePostType = type;
      const config = POST_TYPE_CONFIG[type];
      if (commentPostType) commentPostType.value = type;
      if (commentModalTitle) commentModalTitle.textContent = config.modalTitle;
      if (commentBodyLabel) commentBodyLabel.textContent = config.bodyLabel;
      if (commentBody) commentBody.placeholder = config.bodyPlaceholder;
      if (commentSubmitBtn) commentSubmitBtn.textContent = config.submitLabel;
      if (postMediaLabel) postMediaLabel.textContent = config.mediaLabel || 'Upload Media';
      if (postMediaHelp) postMediaHelp.textContent = config.mediaHelp || 'Upload directly from your device.';
      if (postMediaPickerText) postMediaPickerText.textContent = config.pickerLabel || 'Add media';
	      if (postMediaFile) {
	        postMediaFile.required = false;
	        postMediaFile.accept = config.mediaAccept || '';
	        postMediaFile.value = '';
	      }
	      selectedSourceMediaKey = '';
	      editedPostMediaFile = null;
	      closeMediaEditor();
	      clearMediaPreview();
	      if (postMediaRow) postMediaRow.classList.toggle('is-hidden', !config.requiresMedia);
	      postTypeButtons.forEach((btn) => {
	        const selected = btn.getAttribute('data-post-type') === type;
        btn.classList.toggle('is-active', selected);
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    };

    const resetCommentComposer = () => {
      if (commentForm) commentForm.reset();
      if (postIdeaSelect) postIdeaSelect.value = '';
      if (commentStatus) commentStatus.textContent = '';
      clearMediaPreview();
      applyPostType('comment');
    };

	    const formatFileSizeMb = (bytes) => {
	      const value = Number(bytes);
	      if (!Number.isFinite(value) || value <= 0) return '0.0 MB';
	      return `${(value / (1024 * 1024)).toFixed(1)} MB`;
	    };

	    const mediaTypeForFile = (file, typeHint = '') => {
	      const hint = text(typeHint).toLowerCase();
	      if (hint === 'photo' || hint === 'video') return hint;
	      return String(file?.type || '').startsWith('video/') ? 'video' : 'photo';
	    };

	    const resetPhotoEditorState = () => {
	      photoEditorState = {
	        brightness: 100,
	        contrast: 100,
	        saturation: 100,
	        rotate: 0,
	        flipX: false
	      };
	    };

	    const resetTextOverlayState = () => {
	      textOverlayState = {
	        text: '',
	        color: '#ffffff',
	        size: 44,
	        x: 50,
	        y: 86
	      };
	    };

	    const syncVideoTrimLabels = () => {
	      if (!mediaEditorTrimStartValue || !mediaEditorTrimEndValue) return;
	      mediaEditorTrimStartValue.textContent = `${Number(videoEditorState.start || 0).toFixed(1)}s`;
	      mediaEditorTrimEndValue.textContent = `${Number(videoEditorState.end || 0).toFixed(1)}s`;
	    };

	    const updateTextOverlayPreview = () => {
	      if (!mediaEditorTextOverlay) return;
	      mediaEditorTextOverlay.textContent = textOverlayState.text || '';
	      mediaEditorTextOverlay.style.color = textOverlayState.color;
	      mediaEditorTextOverlay.style.fontSize = `${textOverlayState.size}px`;
	      mediaEditorTextOverlay.style.left = `${textOverlayState.x}%`;
	      mediaEditorTextOverlay.style.top = `${textOverlayState.y}%`;
	      mediaEditorTextOverlay.classList.toggle('is-empty', !textOverlayState.text.trim());
	      if (mediaEditorTextInput) mediaEditorTextInput.value = textOverlayState.text;
	      if (mediaEditorTextColorInput) mediaEditorTextColorInput.value = textOverlayState.color;
	      if (mediaEditorTextSizeInput) mediaEditorTextSizeInput.value = String(textOverlayState.size);
	      if (mediaEditorTextXInput) mediaEditorTextXInput.value = String(textOverlayState.x);
	      if (mediaEditorTextYInput) mediaEditorTextYInput.value = String(textOverlayState.y);
	    };

	    const updatePhotoEditorPreview = () => {
	      if (!mediaEditorImageEl && !mediaEditorVideoEl) return;
	      const transform = `rotate(${photoEditorState.rotate}deg) scaleX(${photoEditorState.flipX ? -1 : 1})`;
	      const filter = `brightness(${photoEditorState.brightness}%) contrast(${photoEditorState.contrast}%) saturate(${photoEditorState.saturation}%)`;
	      if (mediaEditorImageEl) {
	        mediaEditorImageEl.style.transform = transform;
	        mediaEditorImageEl.style.filter = filter;
	      }
	      if (mediaEditorVideoEl) {
	        mediaEditorVideoEl.style.transform = transform;
	        mediaEditorVideoEl.style.filter = filter;
	      }
	      if (mediaEditorBrightnessInput) mediaEditorBrightnessInput.value = String(photoEditorState.brightness);
	      if (mediaEditorContrastInput) mediaEditorContrastInput.value = String(photoEditorState.contrast);
	      if (mediaEditorSaturationInput) mediaEditorSaturationInput.value = String(photoEditorState.saturation);
	      updateTextOverlayPreview();
	    };

	    const setMediaEditorControlsOpen = (open) => {
	      mediaEditorControlsOpen = Boolean(open);
	      if (!mediaEditorControls || !mediaEditorTabBtn) return;
	      mediaEditorControls.classList.toggle('is-collapsed', !mediaEditorControlsOpen);
	      mediaEditorTabBtn.textContent = mediaEditorControlsOpen ? 'Close' : 'Open';
	      mediaEditorTabBtn.setAttribute('aria-expanded', mediaEditorControlsOpen ? 'true' : 'false');
	    };

	    const closeMediaEditorUrl = () => {
	      if (!mediaEditorObjectUrl) return;
	      URL.revokeObjectURL(mediaEditorObjectUrl);
	      mediaEditorObjectUrl = '';
	    };

	    const ensureMediaEditorOverlay = () => {
	      if (mediaEditorOverlay) return mediaEditorOverlay;
	      mediaEditorOverlay = createEl('div', {
	        class: 'media-editor-overlay is-hidden',
	        id: 'mediaEditorOverlay',
	        role: 'dialog',
	        'aria-modal': 'true',
	        'aria-label': 'Media editor'
	      });
	      mediaEditorOverlay.innerHTML = `
	        <button type="button" class="media-editor__toggle" id="mediaEditorToggleBtn" aria-expanded="false">Open</button>
	        <header class="media-editor__controls is-collapsed" id="mediaEditorControls">
	          <div class="media-editor__controls-scroll">
	            <div class="media-editor__group media-editor__group--adjust">
	              <label>Brightness <input type="range" id="mediaEditorBrightness" min="40" max="180" step="1" value="100"></label>
	              <label>Contrast <input type="range" id="mediaEditorContrast" min="40" max="180" step="1" value="100"></label>
	              <label>Saturation <input type="range" id="mediaEditorSaturation" min="0" max="200" step="1" value="100"></label>
	              <button type="button" class="button ghost tiny" id="mediaEditorRotateBtn">Rotate 90</button>
	              <button type="button" class="button ghost tiny" id="mediaEditorFlipBtn">Flip</button>
	              <button type="button" class="button ghost tiny" id="mediaEditorResetBtn">Reset</button>
	            </div>
	            <div class="media-editor__group media-editor__group--text">
	              <label>Text <input type="text" id="mediaEditorTextInput" maxlength="120" placeholder="Add text overlay"></label>
	              <label>Color <input type="color" id="mediaEditorTextColor" value="#ffffff"></label>
	              <label>Size <input type="range" id="mediaEditorTextSize" min="14" max="140" step="1" value="44"></label>
	              <label>X <input type="range" id="mediaEditorTextX" min="0" max="100" step="1" value="50"></label>
	              <label>Y <input type="range" id="mediaEditorTextY" min="0" max="100" step="1" value="86"></label>
	            </div>
	            <div class="media-editor__group media-editor__group--video" data-editor-mode="video">
	              <label>Trim start <input type="range" id="mediaEditorTrimStart" min="0" max="0" step="0.1" value="0"><span id="mediaEditorTrimStartValue">0.0s</span></label>
	              <label>Trim end <input type="range" id="mediaEditorTrimEnd" min="0" max="0" step="0.1" value="0"><span id="mediaEditorTrimEndValue">0.0s</span></label>
	            </div>
	          </div>
	        </header>
	        <button type="button" class="media-editor__back button ghost tiny" id="mediaEditorBackBtn" aria-label="Back">←</button>
	        <main class="media-editor__stage">
	          <div class="media-editor__preview-wrap">
	            <img id="mediaEditorImage" class="media-editor__image is-hidden" alt="Editing preview" />
	            <video id="mediaEditorVideo" class="media-editor__video is-hidden" controls playsinline></video>
	            <div id="mediaEditorTextOverlay" class="media-editor__text is-empty"></div>
	          </div>
	        </main>
	        <button type="button" class="media-editor__apply button" id="mediaEditorApplyBtn">Apply edits</button>
	      `;
	      document.body.appendChild(mediaEditorOverlay);

	      mediaEditorControls = mediaEditorOverlay.querySelector('#mediaEditorControls');
	      mediaEditorTabBtn = mediaEditorOverlay.querySelector('#mediaEditorToggleBtn');
	      mediaEditorBackBtn = mediaEditorOverlay.querySelector('#mediaEditorBackBtn');
	      mediaEditorApplyBtn = mediaEditorOverlay.querySelector('#mediaEditorApplyBtn');
	      mediaEditorStatus = mediaEditorOverlay.querySelector('#mediaEditorStatus');
	      mediaEditorImageEl = mediaEditorOverlay.querySelector('#mediaEditorImage');
	      mediaEditorVideoEl = mediaEditorOverlay.querySelector('#mediaEditorVideo');
	      mediaEditorBrightnessInput = mediaEditorOverlay.querySelector('#mediaEditorBrightness');
	      mediaEditorContrastInput = mediaEditorOverlay.querySelector('#mediaEditorContrast');
	      mediaEditorSaturationInput = mediaEditorOverlay.querySelector('#mediaEditorSaturation');
	      mediaEditorRotateBtn = mediaEditorOverlay.querySelector('#mediaEditorRotateBtn');
	      mediaEditorFlipBtn = mediaEditorOverlay.querySelector('#mediaEditorFlipBtn');
	      mediaEditorResetBtn = mediaEditorOverlay.querySelector('#mediaEditorResetBtn');
	      mediaEditorTextInput = mediaEditorOverlay.querySelector('#mediaEditorTextInput');
	      mediaEditorTextColorInput = mediaEditorOverlay.querySelector('#mediaEditorTextColor');
	      mediaEditorTextSizeInput = mediaEditorOverlay.querySelector('#mediaEditorTextSize');
	      mediaEditorTextXInput = mediaEditorOverlay.querySelector('#mediaEditorTextX');
	      mediaEditorTextYInput = mediaEditorOverlay.querySelector('#mediaEditorTextY');
	      mediaEditorTrimStartInput = mediaEditorOverlay.querySelector('#mediaEditorTrimStart');
	      mediaEditorTrimEndInput = mediaEditorOverlay.querySelector('#mediaEditorTrimEnd');
	      mediaEditorTrimStartValue = mediaEditorOverlay.querySelector('#mediaEditorTrimStartValue');
	      mediaEditorTrimEndValue = mediaEditorOverlay.querySelector('#mediaEditorTrimEndValue');
	      mediaEditorTextOverlay = mediaEditorOverlay.querySelector('#mediaEditorTextOverlay');
	      mediaEditorVideoOnlyControls = Array.from(mediaEditorOverlay.querySelectorAll('[data-editor-mode="video"]'));

	      mediaEditorTabBtn?.addEventListener('click', () => setMediaEditorControlsOpen(!mediaEditorControlsOpen));
	      mediaEditorBackBtn?.addEventListener('click', () => closeMediaEditor());
	      mediaEditorOverlay.addEventListener('click', (evt) => {
	        if (evt.target === mediaEditorOverlay) closeMediaEditor();
	      });
	      document.addEventListener('keydown', (evt) => {
	        if (evt.key === 'Escape' && mediaEditorOverlay && !mediaEditorOverlay.classList.contains('is-hidden')) {
	          closeMediaEditor();
	        }
	      });

	      const onPhotoRangeInput = () => {
	        photoEditorState.brightness = Number(mediaEditorBrightnessInput?.value || 100);
	        photoEditorState.contrast = Number(mediaEditorContrastInput?.value || 100);
	        photoEditorState.saturation = Number(mediaEditorSaturationInput?.value || 100);
	        updatePhotoEditorPreview();
	      };
	      mediaEditorBrightnessInput?.addEventListener('input', onPhotoRangeInput);
	      mediaEditorContrastInput?.addEventListener('input', onPhotoRangeInput);
	      mediaEditorSaturationInput?.addEventListener('input', onPhotoRangeInput);
	      mediaEditorRotateBtn?.addEventListener('click', () => {
	        photoEditorState.rotate = (photoEditorState.rotate + 90) % 360;
	        updatePhotoEditorPreview();
	      });
	      mediaEditorFlipBtn?.addEventListener('click', () => {
	        photoEditorState.flipX = !photoEditorState.flipX;
	        updatePhotoEditorPreview();
	      });
	      mediaEditorResetBtn?.addEventListener('click', () => {
	        resetPhotoEditorState();
	        resetTextOverlayState();
	        updatePhotoEditorPreview();
	      });
	      const onTextOverlayInput = () => {
	        textOverlayState.text = String(mediaEditorTextInput?.value || '');
	        textOverlayState.color = String(mediaEditorTextColorInput?.value || '#ffffff');
	        textOverlayState.size = Number(mediaEditorTextSizeInput?.value || 44);
	        textOverlayState.x = Number(mediaEditorTextXInput?.value || 50);
	        textOverlayState.y = Number(mediaEditorTextYInput?.value || 86);
	        updateTextOverlayPreview();
	      };
	      mediaEditorTextInput?.addEventListener('input', onTextOverlayInput);
	      mediaEditorTextColorInput?.addEventListener('input', onTextOverlayInput);
	      mediaEditorTextSizeInput?.addEventListener('input', onTextOverlayInput);
	      mediaEditorTextXInput?.addEventListener('input', onTextOverlayInput);
	      mediaEditorTextYInput?.addEventListener('input', onTextOverlayInput);

	      const onTrimChange = () => {
	        const maxEnd = Number(videoEditorState.duration || 0);
	        let nextStart = Number(mediaEditorTrimStartInput?.value || 0);
	        let nextEnd = Number(mediaEditorTrimEndInput?.value || 0);
	        if (nextStart < 0) nextStart = 0;
	        if (nextEnd > maxEnd) nextEnd = maxEnd;
	        if (nextStart > nextEnd - 0.1) nextStart = Math.max(0, nextEnd - 0.1);
	        if (nextEnd < nextStart + 0.1) nextEnd = Math.min(maxEnd, nextStart + 0.1);
	        videoEditorState.start = nextStart;
	        videoEditorState.end = nextEnd;
	        if (mediaEditorTrimStartInput) mediaEditorTrimStartInput.value = String(nextStart);
	        if (mediaEditorTrimEndInput) mediaEditorTrimEndInput.value = String(nextEnd);
	        syncVideoTrimLabels();
	      };
	      mediaEditorTrimStartInput?.addEventListener('input', onTrimChange);
	      mediaEditorTrimEndInput?.addEventListener('input', onTrimChange);

	      return mediaEditorOverlay;
	    };

	    const getEditorTransformMeta = (sourceWidth, sourceHeight) => {
	      const rotation = ((photoEditorState.rotate % 360) + 360) % 360;
	      const quarterTurn = rotation === 90 || rotation === 270;
	      const canvasWidth = quarterTurn ? sourceHeight : sourceWidth;
	      const canvasHeight = quarterTurn ? sourceWidth : sourceHeight;
	      return { rotation, canvasWidth, canvasHeight };
	    };

	    const drawEditorFrame = (ctx, source, sourceWidth, sourceHeight, canvasWidth, canvasHeight) => {
	      const rotation = ((photoEditorState.rotate % 360) + 360) % 360;
	      const transform = `brightness(${photoEditorState.brightness}%) contrast(${photoEditorState.contrast}%) saturate(${photoEditorState.saturation}%)`;
	      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
	      ctx.save();
	      ctx.translate(canvasWidth / 2, canvasHeight / 2);
	      ctx.rotate((rotation * Math.PI) / 180);
	      ctx.scale(photoEditorState.flipX ? -1 : 1, 1);
	      ctx.filter = transform;
	      ctx.drawImage(source, -sourceWidth / 2, -sourceHeight / 2, sourceWidth, sourceHeight);
	      ctx.restore();
	      const overlayText = String(textOverlayState.text || '').trim();
	      if (!overlayText) return;
	      const px = (Math.max(0, Math.min(100, Number(textOverlayState.x || 50))) / 100) * canvasWidth;
	      const py = (Math.max(0, Math.min(100, Number(textOverlayState.y || 86))) / 100) * canvasHeight;
	      const fontSize = Math.max(12, Number(textOverlayState.size || 44));
	      ctx.save();
	      ctx.font = `700 ${fontSize}px ${getComputedStyle(document.body).fontFamily || 'sans-serif'}`;
	      ctx.textAlign = 'center';
	      ctx.textBaseline = 'middle';
	      ctx.fillStyle = String(textOverlayState.color || '#ffffff');
	      ctx.strokeStyle = 'rgba(0, 0, 0, 0.58)';
	      ctx.lineWidth = Math.max(2, Math.round(fontSize / 11));
	      ctx.strokeText(overlayText, px, py);
	      ctx.fillText(overlayText, px, py);
	      ctx.restore();
	    };

	    const applyPhotoEditsToFile = async (file) => {
	      const objectUrl = URL.createObjectURL(file);
	      try {
	        const image = await new Promise((resolve, reject) => {
	          const img = new Image();
	          img.onload = () => resolve(img);
	          img.onerror = () => reject(new Error('Could not decode image for editing.'));
	          img.src = objectUrl;
	        });
	        const sourceWidth = Number(image.naturalWidth || image.width || 0);
	        const sourceHeight = Number(image.naturalHeight || image.height || 0);
	        if (!sourceWidth || !sourceHeight) return file;
	        const { canvasWidth, canvasHeight } = getEditorTransformMeta(sourceWidth, sourceHeight);
	        const canvas = document.createElement('canvas');
	        canvas.width = canvasWidth;
	        canvas.height = canvasHeight;
	        const ctx = canvas.getContext('2d');
	        if (!ctx) return file;
	        drawEditorFrame(ctx, image, sourceWidth, sourceHeight, canvas.width, canvas.height);
	        const outputType = ['image/png', 'image/webp', 'image/jpeg'].includes(file.type) ? file.type : 'image/jpeg';
	        const quality = outputType === 'image/png' ? undefined : 0.92;
	        const blob = await new Promise((resolve) => canvas.toBlob(resolve, outputType, quality));
	        if (!blob || !blob.size) return file;
	        const extension = outputType === 'image/png' ? 'png' : (outputType === 'image/webp' ? 'webp' : 'jpg');
	        const baseName = String(file.name || 'photo').replace(/\.[^.]*$/, '') || 'photo';
	        return new File([blob], `${baseName}.${extension}`, {
	          type: outputType,
	          lastModified: Date.now()
	        });
	      } finally {
	        URL.revokeObjectURL(objectUrl);
	      }
	    };

	    const trimVideoFileByRange = async (file, startSec, endSec, options = {}) => {
	      if (typeof MediaRecorder === 'undefined') {
	        throw new Error('Video trimming is not supported in this browser.');
	      }
	      const forceRender = Boolean(options && options.forceRender);
	      const sourceUrl = URL.createObjectURL(file);
	      const video = document.createElement('video');
	      video.src = sourceUrl;
	      video.muted = true;
	      video.playsInline = true;
	      video.preload = 'auto';
	      let canvasStream = null;
	      let recorder = null;
	      let rafId = 0;
	      try {
	        await new Promise((resolve, reject) => {
	          video.onloadedmetadata = () => resolve();
	          video.onerror = () => reject(new Error('Could not load video metadata.'));
	        });
	        const duration = Number(video.duration || 0);
	        if (!duration) return file;
	        const start = Math.max(0, Math.min(duration - 0.1, Number(startSec || 0)));
	        const end = Math.max(start + 0.1, Math.min(duration, Number(endSec || duration)));
	        if (!forceRender && start <= 0.05 && end >= duration - 0.05) return file;
	        const outputFormat = getSafeRecordedVideoFormat();
	        if (!outputFormat) {
	          throw new Error('Video edits are not supported here yet. Upload the original MP4/MOV file without edits.');
	        }
	        const rawSourceWidth = Number(video.videoWidth || 0) || 1280;
	        const rawSourceHeight = Number(video.videoHeight || 0) || 720;
	        const maxWidth = 1280;
	        const scale = Math.min(1, maxWidth / rawSourceWidth);
	        const drawWidth = Math.max(2, Math.round(rawSourceWidth * scale));
	        const drawHeight = Math.max(2, Math.round(rawSourceHeight * scale));
	        const transformMeta = getEditorTransformMeta(drawWidth, drawHeight);
	        const canvas = document.createElement('canvas');
	        canvas.width = transformMeta.canvasWidth;
	        canvas.height = transformMeta.canvasHeight;
	        const ctx = canvas.getContext('2d');
	        if (!ctx) return file;
	        if (typeof canvas.captureStream !== 'function') {
	          throw new Error('Video trimming is not supported in this browser.');
	        }
	        canvasStream = canvas.captureStream(30);
	        const outputMime = outputFormat.mimeType;

	        const chunks = [];
	        recorder = new MediaRecorder(canvasStream, { mimeType: outputMime, videoBitsPerSecond: 2400000 });
	        const done = new Promise((resolve, reject) => {
	          recorder.ondataavailable = (evt) => {
	            if (evt.data && evt.data.size > 0) chunks.push(evt.data);
	          };
	          recorder.onerror = () => reject(new Error('Could not render trimmed video.'));
	          recorder.onstop = () => resolve(chunks.length ? new Blob(chunks, { type: outputMime }) : null);
	        });

	        const drawLoop = () => {
	          if (!video || video.paused || video.ended) return;
	          drawEditorFrame(ctx, video, drawWidth, drawHeight, canvas.width, canvas.height);
	          if (video.currentTime >= end) {
	            video.pause();
	            if (recorder && recorder.state !== 'inactive') recorder.stop();
	            return;
	          }
	          rafId = window.requestAnimationFrame(drawLoop);
	        };

	        await new Promise((resolve, reject) => {
	          const onSeeked = () => {
	            video.removeEventListener('seeked', onSeeked);
	            resolve();
	          };
	          video.addEventListener('seeked', onSeeked);
	          video.onerror = () => reject(new Error('Could not seek video for trim.'));
	          video.currentTime = start;
	        });

	        recorder.start(200);
	        await video.play();
	        drawLoop();
	        const rendered = await done;
	        if (!rendered || !rendered.size) return file;
	        const baseName = String(file.name || 'video').replace(/\.[^.]*$/, '') || 'video';
	        return new File([rendered], `${baseName}.${outputFormat.extension}`, { type: outputMime, lastModified: Date.now() });
	      } finally {
	        if (rafId) window.cancelAnimationFrame(rafId);
	        if (recorder && recorder.state !== 'inactive') {
	          try { recorder.stop(); } catch (_err) {}
	        }
	        if (canvasStream && typeof canvasStream.getTracks === 'function') {
	          canvasStream.getTracks().forEach((track) => track.stop());
	        }
	        try { video.pause(); } catch (_err) {}
	        URL.revokeObjectURL(sourceUrl);
	      }
	    };

	    const applyMediaEdits = async () => {
	      if (!mediaEditorSourceFile) return;
	      const mode = mediaTypeForFile(mediaEditorSourceFile, mediaEditorSourceType);
	      if (mediaEditorApplyBtn) mediaEditorApplyBtn.disabled = true;
	      if (mediaEditorStatus) mediaEditorStatus.textContent = 'Applying edits...';
	      try {
	        let nextFile = mediaEditorSourceFile;
	        if (mode === 'photo') {
	          const changed = photoEditorState.brightness !== 100
	            || photoEditorState.contrast !== 100
	            || photoEditorState.saturation !== 100
	            || photoEditorState.rotate !== 0
	            || photoEditorState.flipX
	            || Boolean(String(textOverlayState.text || '').trim());
	          if (changed) nextFile = await applyPhotoEditsToFile(mediaEditorSourceFile);
	        } else if (mode === 'video') {
	          const duration = Number(videoEditorState.duration || 0);
	          const hasTrimChange = duration > 0
	            && (Math.abs(Number(videoEditorState.start || 0)) > 0.05
	              || Math.abs(Number(videoEditorState.end || duration) - duration) > 0.05);
	          const hasVisualChange = photoEditorState.brightness !== 100
	            || photoEditorState.contrast !== 100
	            || photoEditorState.saturation !== 100
	            || photoEditorState.rotate !== 0
	            || photoEditorState.flipX
	            || Boolean(String(textOverlayState.text || '').trim());
	          const changed = hasTrimChange || hasVisualChange;
	          if (changed) nextFile = await trimVideoFileByRange(
	            mediaEditorSourceFile,
	            Number(videoEditorState.start || 0),
	            Number(videoEditorState.end || duration),
	            { forceRender: hasVisualChange }
	          );
	        }
	        editedPostMediaFile = nextFile;
	        renderMediaPreview(editedPostMediaFile, mode);
	        if (commentStatus) commentStatus.textContent = 'Media edits saved.';
	        closeMediaEditor();
	      } catch (err) {
	        if (mediaEditorStatus) mediaEditorStatus.textContent = err?.message || 'Could not apply edits.';
	      } finally {
	        if (mediaEditorApplyBtn) mediaEditorApplyBtn.disabled = false;
	      }
	    };

	    closeMediaEditor = () => {
	      if (!mediaEditorOverlay) return;
	      mediaEditorOverlay.classList.add('is-hidden');
	      mediaEditorSourceFile = null;
	      mediaEditorSourceType = '';
	      if (mediaEditorVideoEl) {
	        try { mediaEditorVideoEl.pause(); } catch (_err) {}
	        mediaEditorVideoEl.removeAttribute('src');
	        mediaEditorVideoEl.load();
	        mediaEditorVideoEl.style.removeProperty('transform');
	        mediaEditorVideoEl.style.removeProperty('filter');
	      }
	      if (mediaEditorImageEl) {
	        mediaEditorImageEl.removeAttribute('src');
	        mediaEditorImageEl.style.removeProperty('transform');
	        mediaEditorImageEl.style.removeProperty('filter');
	      }
	      if (mediaEditorTextOverlay) {
	        mediaEditorTextOverlay.textContent = '';
	        mediaEditorTextOverlay.classList.add('is-empty');
	      }
	      closeMediaEditorUrl();
	      setMediaEditorControlsOpen(false);
	      document.body.classList.remove('media-editor-open');
	    };

	    openMediaEditor = (file, typeHint = '') => {
	      if (!file) return;
	      ensureMediaEditorOverlay();
	      mediaEditorSourceFile = file;
	      mediaEditorSourceType = mediaTypeForFile(file, typeHint || activePostType);
	      closeMediaEditorUrl();
	      mediaEditorObjectUrl = URL.createObjectURL(file);
	      if (mediaEditorStatus) mediaEditorStatus.textContent = 'Tap Open to reveal edit controls.';
	      setMediaEditorControlsOpen(false);
	      resetPhotoEditorState();
	      resetTextOverlayState();
	      updatePhotoEditorPreview();
	      if (mediaEditorSourceType === 'photo') {
	        mediaEditorVideoOnlyControls.forEach((el) => el.classList.add('is-hidden'));
	        if (mediaEditorVideoEl) {
	          mediaEditorVideoEl.classList.add('is-hidden');
	          mediaEditorVideoEl.removeAttribute('src');
	          mediaEditorVideoEl.load();
	        }
	        if (mediaEditorImageEl) {
	          mediaEditorImageEl.src = mediaEditorObjectUrl;
	          mediaEditorImageEl.classList.remove('is-hidden');
	          updatePhotoEditorPreview();
	        }
	      } else {
	        mediaEditorVideoOnlyControls.forEach((el) => el.classList.remove('is-hidden'));
	        if (mediaEditorImageEl) {
	          mediaEditorImageEl.classList.add('is-hidden');
	          mediaEditorImageEl.removeAttribute('src');
	        }
	        if (mediaEditorVideoEl) {
	          mediaEditorVideoEl.src = mediaEditorObjectUrl;
	          mediaEditorVideoEl.classList.remove('is-hidden');
	          mediaEditorVideoEl.currentTime = 0;
	          mediaEditorVideoEl.onloadedmetadata = () => {
	            const duration = Number(mediaEditorVideoEl.duration || 0);
	            videoEditorState = { start: 0, end: duration, duration };
	            if (mediaEditorTrimStartInput) {
	              mediaEditorTrimStartInput.max = String(duration);
	              mediaEditorTrimStartInput.value = '0';
	            }
	            if (mediaEditorTrimEndInput) {
	              mediaEditorTrimEndInput.max = String(duration);
	              mediaEditorTrimEndInput.value = String(duration);
	            }
	            syncVideoTrimLabels();
	            updatePhotoEditorPreview();
	          };
	        }
	      }
	      mediaEditorApplyBtn?.removeEventListener('click', applyMediaEdits);
	      mediaEditorApplyBtn?.addEventListener('click', applyMediaEdits);
	      mediaEditorOverlay.classList.remove('is-hidden');
	      document.body.classList.add('media-editor-open');
	    };

	    const getUploadErrorText = (xhr) => {
	      const fallback = `Upload failed (${xhr.status || 0})`;
      const responseText = String(xhr.responseText || '').trim();
      if (!responseText) return fallback;
      try {
        const payload = JSON.parse(responseText);
        if (payload && typeof payload.error === 'string' && payload.error.trim()) return payload.error.trim();
      } catch (_err) {
        // use raw response below
      }
      return responseText || fallback;
    };

    const compressImageFile = async (file) => {
      if (!file || !String(file.type || '').startsWith('image/')) return file;
      if (file.size <= 1.5 * 1024 * 1024) return file;

      const imageUrl = URL.createObjectURL(file);
      try {
        const img = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error('Could not read the selected image.'));
          image.src = imageUrl;
        });
        const sourceWidth = Number(img.naturalWidth || img.width || 0);
        const sourceHeight = Number(img.naturalHeight || img.height || 0);
        if (!sourceWidth || !sourceHeight) return file;
        const maxDimension = 1920;
        const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight));
        const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
        const targetHeight = Math.max(1, Math.round(sourceHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.84));
        if (!blob || blob.size <= 0) return file;
        if (blob.size >= file.size * 0.95) return file;
        const baseName = String(file.name || 'upload').replace(/\.[^.]*$/, '') || 'upload';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
      } finally {
        URL.revokeObjectURL(imageUrl);
      }
    };

    const getSafeRecordedVideoFormat = () => {
      if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') return null;
      const candidates = [
        { mimeType: 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', extension: 'mp4' },
        { mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', extension: 'mp4' },
        { mimeType: 'video/mp4', extension: 'mp4' }
      ];
      return candidates.find((candidate) => {
        try {
          return MediaRecorder.isTypeSupported(candidate.mimeType);
        } catch (_err) {
          return false;
        }
      }) || null;
    };

    const transcodeVideoFile = async (file, onStatus = () => {}) => {
      if (!file || !String(file.type || '').startsWith('video/')) return file;
      if (file.size <= 24 * 1024 * 1024) return file;
      if (typeof MediaRecorder === 'undefined') return file;
      const probeVideo = document.createElement('video');
      const captureStreamFn = probeVideo.captureStream || probeVideo.mozCaptureStream;
      if (typeof captureStreamFn !== 'function') return file;
      const outputFormat = getSafeRecordedVideoFormat();
      if (!outputFormat) {
        onStatus('Uploading original video to preserve playback compatibility...');
        return file;
      }
      const outputMime = outputFormat.mimeType;

      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.src = objectUrl;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';

      let stream = null;
      let recorder = null;
      try {
        await new Promise((resolve, reject) => {
          video.onloadedmetadata = () => resolve();
          video.onerror = () => reject(new Error('Could not read the selected video.'));
        });
        if (!Number.isFinite(video.duration) || video.duration <= 0 || video.duration > 120) return file;
        onStatus('Optimizing video for upload...');
        stream = captureStreamFn.call(video);
        const chunks = [];
        recorder = new MediaRecorder(stream, { mimeType: outputMime, videoBitsPerSecond: 2200000 });
        const blobPromise = new Promise((resolve, reject) => {
          recorder.ondataavailable = (evt) => {
            if (evt.data && evt.data.size > 0) chunks.push(evt.data);
          };
          recorder.onerror = () => reject(new Error('Could not optimize the selected video.'));
          recorder.onstop = () => resolve(chunks.length ? new Blob(chunks, { type: outputMime }) : null);
        });

        recorder.start(500);
        await video.play();
        await new Promise((resolve, reject) => {
          video.onended = () => resolve();
          video.onerror = () => reject(new Error('Video optimization was interrupted.'));
        });
        if (recorder.state !== 'inactive') recorder.stop();
        const optimizedBlob = await blobPromise;
        if (!optimizedBlob || optimizedBlob.size <= 0) return file;
        if (optimizedBlob.size >= file.size * 0.95) return file;
        const baseName = String(file.name || 'video').replace(/\.[^.]*$/, '') || 'video';
        return new File([optimizedBlob], `${baseName}.${outputFormat.extension}`, { type: outputMime, lastModified: Date.now() });
      } catch (_err) {
        return file;
      } finally {
        if (recorder && recorder.state !== 'inactive') {
          try { recorder.stop(); } catch (_err) {}
        }
        try { video.pause(); } catch (_err) {}
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((track) => track.stop());
        }
        URL.revokeObjectURL(objectUrl);
      }
    };

    const optimizePostMediaFile = async (file, mediaType, onStatus = () => {}) => {
      if (!file) return null;
      if (mediaType === 'photo') {
        onStatus('Optimizing photo...');
        const optimized = await compressImageFile(file);
        if (optimized && optimized !== file && optimized.size < file.size) {
          const saved = (((file.size - optimized.size) / file.size) * 100).toFixed(0);
          onStatus(`Photo optimized (${saved}% smaller).`);
        }
        return optimized || file;
      }
      if (mediaType === 'video') {
        const optimized = await transcodeVideoFile(file, onStatus);
        if (optimized && optimized !== file && optimized.size < file.size) {
          const saved = (((file.size - optimized.size) / file.size) * 100).toFixed(0);
          onStatus(`Video optimized (${saved}% smaller).`);
        }
        return optimized || file;
      }
      return file;
    };

    const uploadPostMediaFile = (file, mediaType, onProgress = () => {}) => new Promise((resolve, reject) => {
      const token = localStorage.getItem('lnf_token');
      if (!token) {
        reject(new Error('Sign in required.'));
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `/api/uploads/post-media?media_type=${encodeURIComponent(mediaType || '')}`, true);
      xhr.responseType = 'json';
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('Content-Type', file.type || (mediaType === 'video' ? 'video/mp4' : 'image/jpeg'));
      xhr.upload.onprogress = (evt) => {
        if (!evt.lengthComputable) return;
        onProgress(evt.loaded, evt.total);
      };
      xhr.onerror = () => reject(new Error('Network error while uploading media.'));
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const responsePayload = xhr.response && typeof xhr.response === 'object'
            ? xhr.response
            : (() => {
              try { return JSON.parse(xhr.responseText || '{}'); } catch (_err) { return {}; }
            })();
          resolve(responsePayload);
          return;
        }
        reject(new Error(getUploadErrorText(xhr)));
      };
      xhr.send(file);
    });

	    const closeCommentModal = () => {
	      closeMediaEditor();
	      if (!commentModal) return;
	      commentModal.classList.add('is-hidden');
	    };

    const openCommentModal = () => {
      if (!commentModal) return;
      if (!localStorage.getItem('lnf_token')) {
        showSignInGate();
        if (commentStatus) commentStatus.textContent = 'Sign in to post.';
        return;
      }
      resetCommentComposer();
      commentModal.classList.remove('is-hidden');
      commentTopic?.focus();
    };

    if (postIdeaSelect) {
      POST_PROMPTS.forEach((prompt) => {
        postIdeaSelect.appendChild(createEl('option', { value: prompt, text: prompt }));
      });
      postIdeaSelect.addEventListener('change', () => {
        const picked = text(postIdeaSelect.value);
        if (!picked) return;
        if (commentTopic) commentTopic.value = picked;
        commentTopic?.focus();
      });
    }

    postTypeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        applyPostType(btn.getAttribute('data-post-type') || 'comment');
      });
    });
    postMediaFile?.addEventListener('change', syncSelectedMediaPreview);
    postMediaPicker?.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      evt.preventDefault();
      postMediaFile?.click();
    });

    openCommentModalBtn?.addEventListener('click', openCommentModal);
    commentModal?.addEventListener('click', (evt) => {
      if (evt.target === commentModal) closeCommentModal();
    });
    document.body.addEventListener('click', (evt) => {
      const closeBtn = evt.target.closest('[data-close-modal="commentModal"]');
      if (closeBtn) closeCommentModal();
    });
	    document.addEventListener('keydown', (evt) => {
	      if (mediaEditorOverlay && !mediaEditorOverlay.classList.contains('is-hidden')) return;
	      if (evt.key === 'Escape' && commentModal && !commentModal.classList.contains('is-hidden')) {
	        closeCommentModal();
	      }
	    });

	    commentForm?.addEventListener('submit', async (evt) => {
	      evt.preventDefault();
	      const type = text(commentPostType?.value || activePostType || 'comment').toLowerCase();
	      const needsMedia = type === 'photo' || type === 'video';
	      const topicValue = text(commentTopic?.value || '');
	      const bodyValue = text(commentBody?.value || '');
	      const mediaFile = getSelectedMediaFile();

      if (!bodyValue) {
        if (commentStatus) commentStatus.textContent = 'Write your post before submitting.';
        commentBody?.focus();
        return;
      }
      if (needsMedia && !mediaFile) {
        if (commentStatus) commentStatus.textContent = `Upload a ${type} file before posting.`;
        postMediaPicker?.focus();
        return;
      }
      if (mediaFile && type === 'photo' && !String(mediaFile.type || '').startsWith('image/')) {
        if (commentStatus) commentStatus.textContent = 'Please choose an image file for a photo post.';
        return;
      }
      if (mediaFile && type === 'video' && !String(mediaFile.type || '').startsWith('video/')) {
        if (commentStatus) commentStatus.textContent = 'Please choose a video file for a video post.';
        return;
      }
      if (!localStorage.getItem('lnf_token')) {
        if (commentStatus) commentStatus.textContent = 'Sign in to post.';
        showSignInGate();
        return;
      }

      const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
      const topicOut = type === 'comment'
        ? topicValue
        : (topicValue ? `[${typeLabel}] ${topicValue}` : `[${typeLabel}] Shared post`);
      const bodyOut = bodyValue;

	      if (commentSubmitBtn) commentSubmitBtn.disabled = true;
	      if (commentStatus) commentStatus.textContent = 'Preparing post...';
	      try {
	        let mediaData = null;
	        let mediaMimeType = '';
	        if (needsMedia && mediaFile) {
	          const optimizedFile = await optimizePostMediaFile(mediaFile, type, (statusText) => {
	            if (commentStatus && statusText) commentStatus.textContent = statusText;
	          });
          const maxBytes = type === 'video' ? POST_MEDIA_LIMITS.video : POST_MEDIA_LIMITS.photo;
          if (optimizedFile.size > maxBytes) {
            throw new Error(`${typeLabel} is too large (${formatFileSizeMb(optimizedFile.size)}). Max ${formatFileSizeMb(maxBytes)}.`);
          }
          if (commentStatus) commentStatus.textContent = `Uploading ${type}... 0%`;
	          const uploadResult = await uploadPostMediaFile(optimizedFile, type, (loaded, total) => {
	            if (!commentStatus || !total) return;
	            const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
	            commentStatus.textContent = `Uploading ${type}... ${pct}%`;
	          });
	          mediaData = text(uploadResult?.media_data || '');
	          mediaMimeType = text(uploadResult?.mime_type || '');
	          if (!mediaData) throw new Error('Upload finished but no media URL was returned.');
	        }
	        if (commentStatus) commentStatus.textContent = 'Publishing post...';
	        await jsonFetch('/api/comments', {
	          method: 'POST',
	          body: JSON.stringify({
	            body: bodyOut,
	            topic: topicOut,
	            post_type: needsMedia ? type : 'comment',
	            media_data: mediaData || null,
	            media_type: needsMedia ? type : null,
	            mime_type: needsMedia ? mediaMimeType || null : null
	          })
	        });
        if (commentStatus) commentStatus.textContent = 'Posted.';
        closeCommentModal();
        await loadIndexComments();
      } catch (err) {
        if (commentStatus) commentStatus.textContent = err?.message || 'Could not post comment.';
      } finally {
        if (commentSubmitBtn) commentSubmitBtn.disabled = false;
      }
    });

    const renderComments = (rows) => {
      commentList.innerHTML = '';
      if (!rows.length) {
        const signedIn = Boolean(localStorage.getItem('lnf_token'));
        if (activeFeedMode === 'found' && !signedIn) {
          const empty = createEl('div', { class: 'feed-empty-state' });
          empty.appendChild(createEl('p', {
            class: 'muted small',
            text: 'Sign in to view your found feed.'
          }));
          const signInBtn = createEl('button', {
            class: 'button ghost tiny',
            type: 'button',
            text: 'Sign in'
          });
          signInBtn.addEventListener('click', () => showSignInGate());
          empty.appendChild(signInBtn);
          commentList.appendChild(empty);
          return;
        }
        const modeLabel = activeFeedMode === 'found' ? 'found feed' : 'lost feed';
        commentList.innerHTML = `<p class="muted small">No posts in the ${modeLabel} yet.</p>`;
        return;
      }

      const makeActionButton = (iconText, count = 0, extraClass = '') => {
        const btn = createEl('button', {
          class: `feed-action feed-action--icon ${extraClass}`.trim(),
          type: 'button'
        });
        btn.appendChild(createEl('span', { class: 'feed-action__icon', text: iconText, 'aria-hidden': 'true' }));
        btn.appendChild(createEl('span', { class: 'feed-action__count', text: String(count) }));
        return btn;
      };
      const makeMetaActionButton = (iconText, label, extraClass = '') => {
        const btn = createEl('button', {
          class: `feed-action feed-action--meta ${extraClass}`.trim(),
          type: 'button',
          title: label,
          'aria-label': label
        });
        btn.appendChild(createEl('span', { class: 'feed-action__icon', text: iconText, 'aria-hidden': 'true' }));
        btn.appendChild(createEl('span', { class: 'feed-action__label', text: label }));
        return btn;
      };
      const parseTimestamp = (value) => {
        if (!value) return 0;
        const ms = Date.parse(String(value));
        return Number.isFinite(ms) ? ms : 0;
      };
      const getActivePinMeta = (row) => {
        const permanentRaw = row?.pinned_permanent ?? row?.pinnedPermanent;
        const permanent = permanentRaw === true
          || String(permanentRaw || '').trim().toLowerCase() === 'true';
        if (permanent) {
          return { active: true, permanent: true, untilLabel: '' };
        }
        const untilRaw = text(row?.pinned_until ?? row?.pinnedUntil);
        if (!untilRaw) return { active: false, permanent: false, untilLabel: '' };
        const untilMs = parseTimestamp(untilRaw);
        if (!untilMs || untilMs <= Date.now()) return { active: false, permanent: false, untilLabel: '' };
        return {
          active: true,
          permanent: false,
          untilLabel: new Date(untilMs).toLocaleString()
        };
      };

      const rowsToRender = deepLinkPostId ? rows : rows.slice(0, 40);
      let deepLinkCard = null;
      rowsToRender.forEach((row, idx) => {
        const author = text(row.display_name || row.username) || 'Community member';
        const authorId = row.author_id || null;
        if (isBlockedUserId(authorId)) return;
        const avatarUrl = text(row.avatar_url || row.author_avatar || row.profile_image_url);
        const postType = text(row.post_type).toLowerCase();
        const tradeIdText = text(row.trade_id);
        const tradeIdNumber = /^\d+$/.test(tradeIdText) ? Number(tradeIdText) : 0;
        const hasTradeId = Number.isInteger(tradeIdNumber) && tradeIdNumber > 0;
        const hasTradeListingFlag = row.trade_listing === true || row.trade_listing === 'true';
        const tradeImageUrls = parseStringArray(row.trade_image_urls);
        const tradePrimaryImage = text(tradeImageUrls[0] || '');
        const tradeTitleRaw = text(row.trade_title || '');
        const tradeTitle = stripTradeFeedPrefix(tradeTitleRaw || row.topic);
        const tradeDescription = text(row.trade_description || '');
        const tradeContact = text(row.trade_contact || '');
        const tradeCategory = text(row.trade_category || '');
        const tradeRegion = text(row.trade_region || '');
        const hasTradeMeta = Boolean(tradeTitleRaw || tradeDescription || tradeContact || tradeCategory || tradeRegion || tradePrimaryImage || tradeImageUrls.length);
        const isTradeListing = (postType === 'trade_listing' || hasTradeListingFlag) && (hasTradeId || hasTradeMeta)
          || hasTradeId;
        const body = text(row.body);
        const topic = text(row.topic);
        const mediaData = text(row.media_data || '');
        const mediaTypeRaw = text(
          row.media_type
          || row.post_type
          || ''
        );
        const mediaType = mediaTypeRaw === 'video' || mediaTypeRaw === 'photo'
          ? mediaTypeRaw
          : inferFeedMediaType(row, mediaData, mediaTypeRaw);
        const displayMediaData = isTradeListing ? tradePrimaryImage : mediaData;
        const displayMediaType = isTradeListing && tradePrimaryImage ? 'photo' : mediaType;
        const displayVideoMimeType = displayMediaType === 'video'
          ? inferFeedVideoMimeType(row, displayMediaData)
          : '';
        const stampRaw = row.created_at || null;
        const stamp = stampRaw ? new Date(stampRaw).toLocaleString() : '';
        const itemId = text(row.id || `${idx}_${stampRaw || ''}`);
        const numericCommentId = /^\d+$/.test(itemId);
        const isOwnPost = isSelfUserId(authorId);
        const canManagePost = isOwnPost && numericCommentId;
        const pinMeta = getActivePinMeta(row);
        const postId = text(row.post_id);
        const shareUrl = isTradeListing && hasTradeId
          ? new URL(`trade-space.html?trade_id=${encodeURIComponent(tradeIdText)}`, window.location.href).toString()
          : `${window.location.origin}${window.location.pathname}#latestComments`;

        const card = createEl('article', { class: 'feed-post' });
        card.dataset.commentId = itemId;
        card.dataset.postId = postId || itemId;
        const commentKey = normalizeCommentKey(itemId);
        const postKey = normalizeCommentKey(postId || itemId);
        if (!deepLinkApplied && deepLinkPostId && (commentKey === deepLinkPostId || postKey === deepLinkPostId)) {
          card.classList.add('feed-post--search-focus');
          deepLinkCard = card;
        }
        const header = createEl('header', { class: 'feed-post__head' });

        const avatarBtn = createEl('button', {
          class: 'feed-avatar',
          type: 'button',
          'aria-label': `View ${author} profile`
        });
        renderAvatar(avatarBtn, avatarUrl || null, author);
        avatarBtn.addEventListener('click', () => openSharedProfilePreview(authorId, author));

        const meta = createEl('div', { class: 'feed-meta' });
        if (authorId) {
          const authorBtn = createEl('button', { class: 'feed-author', type: 'button', text: author });
          authorBtn.addEventListener('click', () => openSharedProfilePreview(authorId, author));
          meta.appendChild(authorBtn);
        } else {
          meta.appendChild(createEl('div', { class: 'feed-author', text: author }));
        }
        meta.appendChild(createEl('div', { class: 'feed-timestamp', text: stamp || 'just now' }));

        header.appendChild(avatarBtn);
        header.appendChild(meta);
        card.appendChild(header);

        if (pinMeta.active) {
          const pinText = pinMeta.permanent
            ? 'Pinned (permanent)'
            : `Pinned until ${pinMeta.untilLabel}`;
          card.appendChild(createEl('p', { class: 'feed-pin-badge', text: pinText }));
        }
        if (isTradeListing ? tradeTitle : topic) {
          if (isTradeListing && hasTradeId) {
            const topicBtn = createEl('button', {
              class: 'feed-topic feed-topic--link',
              type: 'button',
              text: tradeTitle,
              title: 'Open listing'
            });
            topicBtn.addEventListener('click', () => openTradeListing(tradeIdText));
            card.appendChild(topicBtn);
          } else {
            card.appendChild(createEl('p', { class: 'feed-topic', text: topic }));
          }
        }
        if (isTradeListing) {
          if (tradeDescription) card.appendChild(createEl('p', { class: 'feed-body', text: tradeDescription }));
          else if (body) card.appendChild(createEl('p', { class: 'feed-body', text: body }));
          if (tradeContact) card.appendChild(createEl('p', { class: 'feed-contact', text: tradeContact }));
        } else if (body) {
          card.appendChild(createEl('p', { class: 'feed-body', text: body }));
        }
        if (displayMediaData) {
          if (displayMediaType === 'video') {
            const mediaFrame = createEl('div', { class: 'feed-media-frame' });
            const videoEl = createEl('video', {
              class: 'feed-media feed-media--video',
              controls: 'controls',
              preload: 'metadata',
              playsinline: 'playsinline'
            });
            videoEl.setAttribute('webkit-playsinline', 'webkit-playsinline');
            const sourceAttrs = { src: displayMediaData };
            // Let the browser sniff unsupported/ambiguous labels (e.g. quicktime variants).
            if (displayVideoMimeType && canBrowserPlayVideoMime(displayVideoMimeType)) {
              sourceAttrs.type = displayVideoMimeType;
            }
            videoEl.appendChild(createEl('source', sourceAttrs));
            videoEl.addEventListener('loadedmetadata', () => {
              const duration = Number(videoEl.duration || 0);
              if (!Number.isFinite(duration) || duration <= 0.12) return;
              try {
                videoEl.currentTime = Math.min(0.1, Math.max(0, duration / 10));
              } catch (_) {
                // Leave the browser's default poster behavior alone if seeking is blocked.
              }
            }, { once: true });
            videoEl.addEventListener('error', () => {
              renderFeedVideoFallback(mediaFrame, displayMediaData);
            }, { once: true });
            mediaFrame.appendChild(videoEl);
            try {
              videoEl.load();
            } catch (_err) {
              renderFeedVideoFallback(mediaFrame, displayMediaData);
            }
            card.appendChild(mediaFrame);
          } else {
            if (isTradeListing && hasTradeId) {
              const mediaBtn = createEl('button', {
                class: 'feed-media-button',
                type: 'button',
                title: 'Open listing'
              });
              mediaBtn.appendChild(createEl('img', {
                class: 'feed-media feed-media--photo',
                src: displayMediaData,
                alt: `${topic || 'Trade listing'} main photo`,
                loading: 'lazy'
              }));
              mediaBtn.addEventListener('click', () => openTradeListing(tradeIdText));
              card.appendChild(mediaBtn);
            } else {
              const mediaFrame = createEl('div', { class: 'feed-media-frame' });
              mediaFrame.appendChild(createEl('img', {
                class: 'feed-media feed-media--photo',
                src: displayMediaData,
                alt: `${author} post media`,
                loading: 'lazy'
              }));
              card.appendChild(mediaFrame);
            }
          }
        }
        if (isTradeListing && (tradeCategory || tradeRegion)) {
          const tradePills = createEl('div', { class: 'feed-trade-pills' });
          if (tradeCategory) tradePills.appendChild(createEl('span', { class: 'feed-trade-pill', text: tradeCategory }));
          if (tradeRegion) tradePills.appendChild(createEl('span', { class: 'feed-trade-pill feed-trade-pill--muted', text: tradeRegion }));
          card.appendChild(tradePills);
        }

        const actions = createEl('div', { class: 'feed-actions' });
        const baseLikes = Number(row.like_count ?? row.likes ?? row.likeCount ?? 0) || 0;
        let commentCount = Number(row.comment_count ?? 0) || 0;
        const baseShares = Number(row.share_count ?? row.shares ?? row.shareCount ?? 0) || 0;
        const shareStateKey = `lnf_feed_share_${itemId}`;
        let localShares = Number(localStorage.getItem(shareStateKey) || '0') || 0;
        const likeBtn = makeActionButton('💡', baseLikes, 'feed-action--like');
        const commentBtn = makeActionButton('💬', commentCount, 'feed-action--comment');
        const shareBtn = makeActionButton('📤', baseShares + localShares, 'feed-action--share');
        if (isTradeListing && hasTradeId) {
          const openTradeBtn = makeMetaActionButton('↗', 'Open listing', 'feed-action--open-trade');
          openTradeBtn.addEventListener('click', () => openTradeListing(tradeIdText));
          actions.appendChild(openTradeBtn);
        }

        const likeStateKey = `lnf_feed_like_${itemId}`;
        let liked = localStorage.getItem(likeStateKey) === '1';
        const likeLabel = likeBtn.querySelector('.feed-action__count');
        const likeIcon = likeBtn.querySelector('.feed-action__icon');
        const commentLabel = commentBtn.querySelector('.feed-action__count');
        const shareLabel = shareBtn.querySelector('.feed-action__count');
        const refreshLikeLabel = () => {
          const display = baseLikes + (liked ? 1 : 0);
          if (likeLabel) likeLabel.textContent = String(display);
          if (likeIcon) likeIcon.textContent = '💡';
          likeBtn.classList.toggle('is-active', liked);
        };
        refreshLikeLabel();
        likeBtn.addEventListener('click', () => {
          if (!isSignedIn()) { showSignInGate(); return; }
          liked = !liked;
          localStorage.setItem(likeStateKey, liked ? '1' : '0');
          refreshLikeLabel();
        });

        const thread = createEl('section', { class: 'feed-thread is-hidden' });
        const threadList = createEl('div', { class: 'feed-thread__list' });
        const threadStatus = createEl('p', { class: 'muted small feed-thread__status', text: '' });
        const threadForm = createEl('form', { class: 'feed-thread__composer' });
        const threadInput = createEl('input', {
          class: 'feed-thread__input',
          type: 'text',
          placeholder: 'Write a comment...',
          autocomplete: 'off'
        });
        const threadSend = createEl('button', {
          class: 'feed-thread__send',
          type: 'submit',
          'aria-label': 'Post comment'
        });
        threadSend.appendChild(createEl('span', { text: '↑', 'aria-hidden': 'true' }));
        threadForm.appendChild(threadInput);
        threadForm.appendChild(threadSend);
        attachMentionAutocomplete(threadInput);
        thread.appendChild(threadList);
        thread.appendChild(threadStatus);
        thread.appendChild(threadForm);

        let threadLoaded = false;
        const renderThreadItems = (items) => {
          threadList.innerHTML = '';
          commentCount = Array.isArray(items) ? items.length : 0;
          if (commentLabel) commentLabel.textContent = String(commentCount);
          if (!Array.isArray(items) || !items.length) {
            threadList.appendChild(createEl('p', { class: 'muted small', text: 'No comments yet. Be the first one.' }));
            return;
          }
          items.forEach((item) => {
            const itemAuthor = text(item.display_name || item.username) || 'Community member';
            const itemBody = text(item.body);
            const itemStampRaw = item.created_at || null;
            const itemStamp = itemStampRaw ? new Date(itemStampRaw).toLocaleString() : '';
            const itemRow = createEl('article', { class: 'feed-thread__item' });
            itemRow.appendChild(createEl('p', { class: 'feed-thread__author', text: itemAuthor }));
            itemRow.appendChild(createEl('p', { class: 'feed-thread__body', text: itemBody || 'No comment text.' }));
            if (itemStamp) itemRow.appendChild(createEl('p', { class: 'feed-thread__stamp', text: itemStamp }));
            threadList.appendChild(itemRow);
          });
        };

        const loadThread = async () => {
          if (threadLoaded) return;
          threadStatus.textContent = 'Loading comments...';
          const threadId = text(postId || itemId);
          if (!threadId) {
            renderThreadItems([]);
            threadStatus.textContent = '';
            threadLoaded = true;
            return;
          }
          const rowsForThread = await jsonFetch(`/api/comments/${encodeURIComponent(threadId)}/replies?limit=100`);
          renderThreadItems(rowsForThread);
          threadStatus.textContent = '';
          threadLoaded = true;
        };

        commentBtn.addEventListener('click', async () => {
          const opening = thread.classList.contains('is-hidden');
          thread.classList.toggle('is-hidden');
          commentBtn.classList.toggle('is-active', opening);
          if (opening) {
            await loadThread();
            threadInput.focus();
          }
        });

        threadForm.addEventListener('submit', async (evt) => {
          evt.preventDefault();
          const value = text(threadInput.value);
          if (!value) return;
          if (!localStorage.getItem('lnf_token')) {
            showSignInGate();
            threadStatus.textContent = 'Sign in required.';
            return;
          }
          threadStatus.textContent = 'Posting...';
          try {
            const threadId = text(postId || itemId);
            if (!threadId) throw new Error('Missing post id');
            await jsonFetch(`/api/comments/${encodeURIComponent(threadId)}/replies`, {
              method: 'POST',
              body: JSON.stringify({ body: value })
            });
            threadInput.value = '';
            threadLoaded = false;
            await loadThread();
            threadStatus.textContent = 'Posted.';
            setTimeout(() => { threadStatus.textContent = ''; }, 900);
          } catch (_) {
            threadStatus.textContent = 'Could not post comment.';
          }
        });

        shareBtn.addEventListener('click', async () => {
          if (!isSignedIn()) { showSignInGate(); return; }
          localShares += 1;
          localStorage.setItem(shareStateKey, String(localShares));
          if (shareLabel) shareLabel.textContent = String(baseShares + localShares);
          const payload = {
            title: topic || `Post from ${author}`,
            text: (body || '').slice(0, 180),
            url: shareUrl
          };
          try {
            if (navigator.share) {
              await navigator.share(payload);
              return;
            }
            if (navigator.clipboard?.writeText) {
              await navigator.clipboard.writeText(payload.url);
              const prev = shareBtn.textContent;
              shareBtn.textContent = 'Copied';
              setTimeout(() => { shareBtn.textContent = prev; }, 900);
            }
          } catch (_) {
            // ignore aborted shares
          }
        });

        if (viewerIsAdmin && numericCommentId) {
          const pinBtn = makeMetaActionButton('📌', pinMeta.active ? 'Unpin' : 'Pin', 'feed-action--pin');
          pinBtn.addEventListener('click', async () => {
            pinBtn.disabled = true;
            try {
              if (pinMeta.active) {
                if (!await uiConfirm('Remove pin from this post?', { title: 'Unpin post', confirmText: 'Unpin' })) return;
                await jsonFetch(`/api/comments/${encodeURIComponent(itemId)}/pin`, { method: 'DELETE' });
              } else {
                const pinInput = await uiPrompt({
                  title: 'Pin post',
                  message: 'Pin duration: enter 1-7 days, or "permanent".',
                  label: 'Duration',
                  value: '7',
                  confirmText: 'Pin'
                });
                if (!pinInput.confirmed) return;
                const value = text(pinInput.value).toLowerCase();
                const pinPayload = {};
                if (value === 'permanent' || value === 'perm' || value === 'forever') {
                  pinPayload.permanent = true;
                } else {
                  const days = Number(value);
                  if (!Number.isFinite(days) || days < 1 || days > 7) {
                    await uiAlert('Enter a number from 1 to 7, or "permanent".', 'Invalid pin duration');
                    return;
                  }
                  pinPayload.days = Math.round(days);
                }
                await jsonFetch(`/api/comments/${encodeURIComponent(itemId)}/pin`, {
                  method: 'POST',
                  body: JSON.stringify(pinPayload)
                });
              }
              await loadIndexComments();
            } catch (err) {
              await uiAlert(getErrorMessage(err, 'Could not update pin.'), 'Pin update failed');
            } finally {
              pinBtn.disabled = false;
            }
          });
          actions.appendChild(pinBtn);
        }

        if (canManagePost) {
          const editBtn = makeMetaActionButton('✎', 'Edit', 'feed-action--edit');
          const deleteBtn = makeMetaActionButton('🗑', 'Delete', 'feed-action--delete');
          editBtn.addEventListener('click', async () => {
            const editInput = await uiForm({
              title: 'Edit post',
              message: 'Update title and text.',
              confirmText: 'Save changes',
              fields: [
                { name: 'topic', label: 'Post title', value: topic || '', placeholder: 'Post title' },
                { name: 'body', type: 'textarea', label: 'Post text', value: body || '', placeholder: 'Post text', required: true }
              ]
            });
            if (!editInput.confirmed) return;
            const payload = {
              topic: text(editInput.values.topic),
              body: text(editInput.values.body)
            };
            if (!payload.body && !mediaData) {
              await uiAlert('Post text is required for this post.', 'Post text required');
              return;
            }
            try {
              await jsonFetch(`/api/comments/${encodeURIComponent(itemId)}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
              });
              await loadIndexComments();
            } catch (err) {
              await uiAlert(getErrorMessage(err, 'Could not edit post.'), 'Edit failed');
            }
          });
          deleteBtn.addEventListener('click', async () => {
            if (!await uiConfirm('Delete this post?', { title: 'Delete post', confirmText: 'Delete' })) return;
            try {
              await jsonFetch(`/api/comments/${encodeURIComponent(itemId)}`, { method: 'DELETE' });
              await loadIndexComments();
            } catch (err) {
              await uiAlert(getErrorMessage(err, 'Could not delete post.'), 'Delete failed');
            }
          });
          actions.appendChild(editBtn);
          actions.appendChild(deleteBtn);
        }

        actions.appendChild(likeBtn);
        actions.appendChild(commentBtn);
        actions.appendChild(shareBtn);
        card.appendChild(actions);
        card.appendChild(thread);
        commentList.appendChild(card);
      });

      if (deepLinkCard && !deepLinkApplied) {
        deepLinkApplied = true;
        window.requestAnimationFrame(() => {
          deepLinkCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
          window.setTimeout(() => deepLinkCard.classList.remove('feed-post--search-focus'), 8000);
        });
      }
    };

    const loadIndexComments = async () => {
      await loadViewerContext();
      if (coerceGuestFeedModeToLost()) {
        persistHomeFeedQueryState();
      }
      syncHomeFeedControls();
      const signedIn = Boolean(localStorage.getItem('lnf_token'));
      if (activeFeedMode === 'found' && !signedIn) {
        renderComments([]);
        return;
      }
      try {
        const rows = asArray(await jsonFetch(buildCommentFeedEndpoint()));
        renderComments(rows);
        syncHomeFeedStatus();
      } catch (_err) {
        renderComments([]);
        syncHomeFeedStatus('Could not load this feed right now.');
      }
    };

    let indexCommentsLoaded = false;
    const ensureIndexCommentsLoaded = () => {
      if (indexCommentsLoaded) return;
      indexCommentsLoaded = true;
      loadIndexComments().catch(() => {});
    };

    homeFeedControls?.addEventListener('click', (evt) => {
      const modeBtn = evt.target.closest('[data-feed-mode]');
      if (!modeBtn) return;
      const nextMode = normalizeFeedMode(modeBtn.getAttribute('data-feed-mode'));
      const signedIn = Boolean(localStorage.getItem('lnf_token'));
      if (nextMode === 'found' && !signedIn) {
        showSignInGate();
        syncHomeFeedStatus('Found feed requires sign-in.');
        return;
      }
      if (nextMode !== activeFeedMode) {
        activeFeedMode = nextMode;
      }

      syncHomeFeedControls();
      persistHomeFeedQueryState();
      if (indexCommentsLoaded) {
        loadIndexComments().catch(() => {});
      } else {
        ensureIndexCommentsLoaded();
      }
    });
    window.addEventListener('lnf:friendship-changed', () => {
      if (activeFeedMode !== 'found') return;
      if (!indexCommentsLoaded) return;
      loadIndexComments().catch(() => {});
    });

    syncHomeFeedControls();

    if (deepLinkPostId) {
      ensureIndexCommentsLoaded();
    } else if ('IntersectionObserver' in window) {
      const latestCommentsSection = $('#latestComments') || commentList.closest('section') || commentList;
      const feedObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        feedObserver.disconnect();
        ensureIndexCommentsLoaded();
      }, { rootMargin: '280px 0px' });
      if (latestCommentsSection) feedObserver.observe(latestCommentsSection);
      else ensureIndexCommentsLoaded();
    } else {
      ensureIndexCommentsLoaded();
    }
  }

});
